  import React, { useState, useEffect, useRef } from 'react';
  import ReactDOM from 'react-dom';
  import { motion, AnimatePresence } from 'motion/react';
  import {
    Search, ChevronRight, Filter, X,
    MessageSquare, Camera, Check, ChevronLeft, MoreHorizontal, RefreshCw, AlertCircle, Edit, Plus, Loader2,
    Settings, Share2, Play, TrendingUp, Link, Copy, Trash2, Wallet, FileText, DollarSign
  } from 'lucide-react';
  import Sidebar from '../components/Sidebar';
  import TopBar from '../components/TopBar';
  import EditTradeModal from '../components/EditTradeModal';
  import { usePortfolio } from '../context/PortfolioContext';
  import { useCexAccount, ALL_ACCOUNTS_ID } from '../context/CexAccountContext';
  import { apiService } from '../services/apiService';
  import { SUPPORTED_CEX } from '../constants';
  import { ProductTour, TourStep } from '../components/ProductTour';
  import { SettingsModal } from '../components/SettingsModal';
  import { API_BASE_URL } from '../config/api';
  import { useSubscription } from '../context/SubscriptionContext';
  import { TradingViewWidget } from '../components/TradingViewWidget';

  interface TradesPageProps {
    formData: any;
    pendingTrade?: any;
    onPendingTradeConsumed?: () => void;
  }

  const getExchangeInfo = (cexName: string) => {
    return SUPPORTED_CEX.find(c => c.value.toLowerCase() === (cexName || '').toLowerCase());
  };

  const formatCurrency = (value: string | number): string => {
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
    if (isNaN(numValue)) return '$0.00';
    const absValue = Math.abs(numValue);
    const formatted = absValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 });
    return numValue < 0 ? `-$${formatted}` : `$${formatted}`;
  };

  const getTradingViewSymbol = (pairs: string) => {
    const cleanPairs = pairs.replace(/[\s/]/g, '').toUpperCase();
    if (cleanPairs.endsWith('IDRT')) {
      const base = cleanPairs.replace('IDRT', '');
      return `INDODAX:${base}IDR`;
    }
    if (cleanPairs.endsWith('IDR')) {
      return `INDODAX:${cleanPairs}`;
    }
    return `BINANCE:${cleanPairs}`;
  };

  const StatRow = ({ label, value, valueColor, fontMono = false }: { label: string, value: any, valueColor?: string, fontMono?: boolean }) => (
    <div className="flex justify-between items-center px-4 py-3.5 text-[11px] border-b border-neutral-900 last:border-b-0">
      <span className="text-neutral-500 font-bold uppercase tracking-wider">{label}</span>
      <span className={`font-black ${valueColor || 'text-white'} ${fontMono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );

  import { useSEO } from '../hooks/useSEO';
  import { useTheme } from '../hooks/useTheme';

  const TradesPage: React.FC<TradesPageProps> = ({ formData, pendingTrade, onPendingTradeConsumed }) => {
    useSEO({
      title: 'Trade Log & Journal',
      description: 'Kelola transaksi trading crypto Anda secara manual atau otomatis via sinkronisasi API CEX. Tambahkan grafik, screenshot, emosi, dan catatan pada setiap jurnal transaksi.',
      keywords: 'trade log, crypto journal, order history, trade list, manual trade input'
    });
    const themeValue = useTheme();
    const isDarkMode = themeValue ?? true; // Fallback to dark mode if undefined
    const { portfolioData, setPortfolioData, lastSyncedAccountId, setLastSyncedAccountId } = usePortfolio();
    const { effectivePlan } = useSubscription();
    const { selectedAccount, accounts, selectedWalletAccount } = useCexAccount();

    const tourSteps: TourStep[] = [
      {
        target: 'body',
        title: "Welcome to the Transactions Page!",
        content: "Here you can monitor and manage all your trading positions, whether automatically synced from exchanges or entered manually.",
        position: 'center'
      },
      {
        target: '[data-tour="sidebar-newtrade"]',
        title: "Add Manual Transaction",
        content: "Manually add your new trade. Now supporting both SPOT & FUTURE (Long/Short) options, complete with leverage size!",
        position: 'right'
      },
      {
        target: '[data-tour="trades-sync"]',
        title: "Live Exchange Account Sync",
        content: "Click the Re-sync button anytime to pull the latest transaction history and open positions in real-time from your exchanges.",
        position: 'bottom'
      },
      {
        target: '[data-tour="trades-table"]',
        title: "Position History List",
        content: "A transparent table recording entry price, exit price, position size, leverage, P&L, and journal strategy setups.",
        position: 'top'
      },
      {
        target: '[data-tour="trades-edit"]',
        title: "Edit Transaction Details",
        content: "Click the Edit button (pencil icon) that appears when you hover over a manual transaction row to change the trade parameter details.",
        position: 'left'
      },
      {
        target: '[data-tour="trades-expand"]',
        title: "In-Depth Transaction Details (Expand)",
        content: "Click the right arrow icon (Expand) to open the premium interactive trading detail panel. Inside, you will find a TradingView Chart, technical notes, and execution audit logs.",
        position: 'left'
      },
      {
        target: 'body',
        title: "Interactive Analysis & Replay Feature",
        content: "Within the detail panel, you can use the 'Replay' button to play back the candlestick chart movement when the price touched your entry/exit levels. This is extremely useful for professionally training your trading psychology!",
        position: 'center'
      }
    ];

    const [selectedTrade, setSelectedTrade] = useState<any | null>(null);
    const [dexLivePrice, setDexLivePrice] = useState<number | null>(null);
    const [resolvedPairAddress, setResolvedPairAddress] = useState<string | null>(null);
    const [resolvedChainId, setResolvedChainId] = useState<string | null>(null);
    const [isResolvingPair, setIsResolvingPair] = useState<boolean>(false);
    const [editingTrade, setEditingTrade] = useState<any | null>(null);
    const [deletingTradeId, setDeletingTradeId] = useState<number | null>(null);
    const [confirmDeleteTradeId, setConfirmDeleteTradeId] = useState<number | null>(null);
    const [leftTab, setLeftTab] = useState<'Stats' | 'Strategy' | 'Executions' | 'Attachments'>('Stats');
    const [rightTab, setRightTab] = useState<'Chart' | 'Notes' | 'Running P&L'>('Chart');
    const [activeModal, setActiveModal] = useState<'chart' | 'notes' | 'pnl' | null>(null);
    const [trades, setTrades] = useState<any[]>([]);
    const [isLoadingTrades, setIsLoadingTrades] = useState(false);
    const [filterCexIds, setFilterCexIds] = useState<string[]>(['All']);
    const [filterDate, setFilterDate] = useState('All Time');
    const [filterSide, setFilterSide] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);
    const TRADES_PER_PAGE = 15;
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncError, setSyncError] = useState('');
    const [dataErrors, setDataErrors] = useState<Record<string, string>>({});
    const [isSavingToDb, setIsSavingToDb] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [replayTrade, setReplayTrade] = useState<any | null>(null);
    const [reviewedTrades, setReviewedTrades] = useState<Record<number, boolean>>(() => {
      try {
        const saved = localStorage.getItem('reviewed_trades');
        return saved ? JSON.parse(saved) : {};
      } catch {
        return {};
      }
    });

    // Attachment upload state: tradeId -> list of uploaded image URLs
    const [tradeAttachments, setTradeAttachments] = useState<Record<string, string[]>>({});
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const [copiedUrlIdx, setCopiedUrlIdx] = useState<string | null>(null);
    const attachmentInputRef = useRef<HTMLInputElement>(null);

    const handleCopyUrl = (url: string, key: string) => {
      navigator.clipboard.writeText(url).then(() => {
        setCopiedUrlIdx(key);
        setTimeout(() => setCopiedUrlIdx(null), 2000);
      });
    };

    const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || !selectedTrade) return;
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      setUploadingAttachment(true);
      for (const file of Array.from(files) as File[]) {
        if (file.size > 5 * 1024 * 1024) {
          alert('File too large. Max 5MB per image.');
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        try {
          const res = await fetch(`${API_BASE_URL}/api/upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: fd,
          });
          const data = await res.json();
          if (res.ok && data.url) {
            setTradeAttachments(prev => ({
              ...prev,
              [selectedTrade.id]: [...(prev[selectedTrade.id] || []), data.url],
            }));
          }
        } catch (err) {
          console.error('Attachment upload failed:', err);
        }
      }
      setUploadingAttachment(false);
      // Reset input so same file can be re-uploaded if needed
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    };

    const handleToggleReview = (tradeId: number) => {
      const updated = { ...reviewedTrades, [tradeId]: !reviewedTrades[tradeId] };
      setReviewedTrades(updated);
      localStorage.setItem('reviewed_trades', JSON.stringify(updated));
      
      if (updated[tradeId]) {
        setSuccessMessage("Trade marked as Reviewed! Success log generated.");
      } else {
        setSuccessMessage("Trade marked as Pending Review.");
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    };

    const handleShareTrade = (pairs: string, pnl: number) => {
      const mockLink = `https://catatcrypto.com/share/trade/${Math.random().toString(36).slice(2, 8)}`;
      navigator.clipboard.writeText(mockLink);
      setSuccessMessage(`Share link copied: ${pairs} (${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)})! Post it on your Discord!`);
      setTimeout(() => setSuccessMessage(null), 4000);
    };

    // Dynamic CEX filter options
    const cexFilterOptions = React.useMemo(() => {
      const list = [
        { id: 'All', label: 'All Trades', icon: null, color: '#ef4444' }
      ];

      // Add each active account
      (accounts || []).forEach(acc => {
        const config = getExchangeInfo(acc.cex_name);
        list.push({
          id: acc.id.toString(),
          label: acc.full_display_name,
          icon: config?.icon || null,
          color: config?.color || '#3b82f6'
        });
      });

      // Add Manual Trades option
      list.push({ id: 'Manual', label: 'Manual Trades', icon: null, color: '#10b981' });

      return list;
    }, [accounts]);

    const getProfitFactor = () => {
      const backendVal = parseFloat(portfolioData?.rrRatio?.split(': ')[1] || '0');
      if (backendVal > 0) {
        return backendVal;
      }

      const winningPnls: number[] = [];
      const losingPnls: number[] = [];

      const dbTrades = (trades || []).filter((t: any) => t._source !== 'mock');
      const apiTrades = portfolioData?.activeDeployments || [];

      const processTradePnls = (pnlStrOrNum: any) => {
        if (pnlStrOrNum === null || pnlStrOrNum === undefined) return;
        const cleanStr = typeof pnlStrOrNum === 'string'
          ? pnlStrOrNum.replace('+', '').replace('$', '').trim()
          : pnlStrOrNum;
        const pnlNum = parseFloat(cleanStr);
        if (!isNaN(pnlNum)) {
          if (pnlNum > 0) {
            winningPnls.push(pnlNum);
          } else if (pnlNum < 0) {
            losingPnls.push(Math.abs(pnlNum));
          }
        }
      };

      if (dbTrades.length > 0) {
        dbTrades.forEach((t: any) => processTradePnls(t.pnl_amount));
      } else if (apiTrades.length > 0) {
        apiTrades.forEach((t: any) => processTradePnls(t.pnl));
      }

      if (winningPnls.length === 0) {
        return 0;
      }

      const sumWin = winningPnls.reduce((sum, val) => sum + val, 0);
      const avgWin = sumWin / winningPnls.length;

      if (losingPnls.length === 0) {
        return 99.99;
      }

      const sumLoss = losingPnls.reduce((sum, val) => sum + val, 0);
      const avgLoss = sumLoss / losingPnls.length;

      return avgWin / avgLoss;
    };

    // Validate portfolio data and set errors
    const validatePortfolioData = (data: any) => {
      const errors: Record<string, string> = {};

      // 1. Net PNL
      if (!data?.netPnl || data.netPnl === "$0.00") {
        errors.netPnl = "Belum ada riwayat trading";
      }

      // 2. Win Rate
      if (!data?.tradeWinRate || data.tradeWinRate === "0%") {
        errors.winRate = "Belum ada data trading untuk dihitung";
      }

      // 3. Loss Rate
      if ((!data?.tradeWinRate || data.tradeWinRate === "0%") && (!data?.activeDeployments || data.activeDeployments.length === 0)) {
        errors.lossRate = "Tidak ada data untuk dihitung";
      }

      // 4. Profit Factor
      const profitFactorVal = getProfitFactor();
      if (profitFactorVal === 0) {
        errors.profitFactor = "Belum ada data trading untuk dihitung";
      }

      // 5. Trades Table
      if ((!data?.activeDeployments || data.activeDeployments.length === 0) && (!trades || trades.length === 0)) {
        errors.tradesTable = "API terhubung tapi belum ada posisi terbuka";
      }

      setDataErrors(errors);
    };

    // Fetch trades from API
    const fetchTrades = async () => {
      setIsLoadingTrades(true);
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return [];

        const params = new URLSearchParams();
        if (selectedWalletAccount) {
          params.append('wallet_account_id', selectedWalletAccount.id.toString());
        } else if (selectedAccount && selectedAccount.id !== ALL_ACCOUNTS_ID) {
          params.append('cex_account_id', selectedAccount.id.toString());
        }

        const response = await fetch(`${API_BASE_URL}/api/trades?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setTrades(data.data);
            return data.data;
          }
        }
        return [];
      } catch (error) {
        console.error('Failed to fetch trades:', error);
        return [];
      } finally {
        setIsLoadingTrades(false);
      }
    };


    // Save trade manual fields
    const handleSaveTrade = async (tradeId: number, data: any) => {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('Not authenticated');

      // Check if this is an API-sourced trade (not in database yet)
      const trade = combinedTrades.find((t: any) => t.id === tradeId);
      if (trade?._source === 'api') {
        alert('This trade is from API and not yet saved to database. Please sync your account first to save trades to database.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/trades/${tradeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save trade');
      }

      // Refresh trades list
      const updatedTrades = await fetchTrades();

      // Refresh metrics via fast-mode sync so portfolioData reflects edited trade
      handleResync(false, true);

      // Dynamically update selectedTrade if it's currently open!
      if (selectedTrade && selectedTrade.id === tradeId) {
        const freshlySaved = updatedTrades.find((t: any) => t.id === tradeId);
        if (freshlySaved) {
          setSelectedTrade(freshlySaved);
        }
      }
    };

    const handleDeleteTrade = async (tradeId: number) => {
      setDeletingTradeId(tradeId);
      setConfirmDeleteTradeId(null);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/api/trades/${tradeId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok || response.status === 204) {
          if (selectedTrade?.id === tradeId) setSelectedTrade(null);
          await fetchTrades();
        }
      } catch (err) {
        console.error('Failed to delete trade:', err);
      } finally {
        setDeletingTradeId(null);
      }
    };

    // Fetch trades when component mounts or selected account/wallet changes
    useEffect(() => {
      fetchTrades();
    }, [selectedAccount, selectedWalletAccount]);

    // Auto-sync when account changes OR on first mount
    const isFirstMount = React.useRef(true);
    useEffect(() => {
      if (effectivePlan === 'expired') {
        return;
      }

      let selectedAccountId = 'cex-all';
      if (selectedWalletAccount) {
        selectedAccountId = `wallet-${selectedWalletAccount.id}`;
      } else if (selectedAccount) {
        selectedAccountId = selectedAccount.id === ALL_ACCOUNTS_ID ? 'cex-all' : `cex-${selectedAccount.id}`;
      }

      // Skip sync if we already have data for THIS exact account on first mount
      if (isFirstMount.current) {
        isFirstMount.current = false;
        if (portfolioData && lastSyncedAccountId === selectedAccountId) {
          console.log('✅ TradesPage: Found valid data on first mount, skipping sync');
          return;
        }
        console.log('🔄 TradesPage: Initial auto-sync for account ID:', selectedAccountId);
        handleResync(false, selectedAccountId === 'cex-all');
        return;
      }

      // Subsequent changes (manual account switch): Use fast/local view if account changed, don't force a live API call!
      if (selectedAccountId !== lastSyncedAccountId) {
        console.log('🔄 TradesPage: Account changed, loading local cached data for ID:', selectedAccountId);
        handleResync(false, true);
      }
    }, [selectedAccount?.id, selectedWalletAccount?.id, lastSyncedAccountId]);

    // Resolve DEX pair address dynamically if missing
    useEffect(() => {
      if (!selectedTrade) {
        setResolvedPairAddress(null);
        setResolvedChainId(null);
        return;
      }

      if (!selectedTrade.is_dex) {
        setResolvedPairAddress(null);
        setResolvedChainId(null);
        return;
      }

      if (selectedTrade.dex_pair_address && selectedTrade.dex_chain) {
        setResolvedPairAddress(selectedTrade.dex_pair_address);
        setResolvedChainId(selectedTrade.dex_chain);
        return;
      }

      if (selectedTrade.dex_token_address) {
        let cancelled = false;
        const resolvePair = async () => {
          setIsResolvingPair(true);
          try {
            const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${selectedTrade.dex_token_address}`);
            if (res.ok) {
              const data = await res.json();
              const pairs = data?.pairs ?? [];
              if (pairs.length > 0 && !cancelled) {
                // Sort pairs by liquidity desc
                pairs.sort((a: any, b: any) => {
                  const liqA = a.liquidity?.usd ?? 0;
                  const liqB = b.liquidity?.usd ?? 0;
                  return liqB - liqA;
                });
                const bestPair = pairs[0];
                setResolvedPairAddress(bestPair.pairAddress);
                setResolvedChainId(bestPair.chainId);
                return;
              }
            }
          } catch (err) {
            console.error("Failed to resolve DEX pair address:", err);
          } finally {
            if (!cancelled) setIsResolvingPair(false);
          }
          if (!cancelled) {
            setResolvedPairAddress(null);
            setResolvedChainId(null);
          }
        };
        resolvePair();
        return () => { cancelled = true; };
      } else {
        setResolvedPairAddress(null);
        setResolvedChainId(null);
      }
    }, [selectedTrade?.id, selectedTrade?.dex_pair_address, selectedTrade?.dex_token_address, selectedTrade?.dex_chain]);

    // Live price for OPEN DEX trades — poll DexScreener every 30s
    useEffect(() => {
      const activeChain = selectedTrade?.dex_chain || resolvedChainId;
      const activePair = selectedTrade?.dex_pair_address || resolvedPairAddress;

      if (!selectedTrade?.is_dex || selectedTrade?.status !== 'OPEN' || !activePair || !activeChain) {
        setDexLivePrice(null);
        return;
      }
      let cancelled = false;
      const fetchPrice = async () => {
        try {
          const res = await fetch(`https://api.dexscreener.com/latest/dex/pairs/${activeChain}/${activePair}`);
          const data = await res.json();
          const price = parseFloat(data?.pairs?.[0]?.priceUsd ?? '0');
          if (!cancelled && price > 0) setDexLivePrice(price);
        } catch { /* ignore */ }
      };
      fetchPrice();
      const interval = setInterval(fetchPrice, 30000);
      return () => { cancelled = true; clearInterval(interval); };
    }, [selectedTrade?.id, selectedTrade?.status, resolvedPairAddress, resolvedChainId]);

    // Auto-open the newly saved manual trade when navigated here from wizard
    useEffect(() => {      if (!pendingTrade) return;
      const open = async () => {
        const updated = await fetchTrades();

        // Also trigger a fast-mode re-sync so metrics reflect the new manual trade
        handleResync(false, true);

        if (updated && updated.length > 0) {
          // Try to match by trade_group_id first, then fall back to most recent
          const matched = updated.find((t: any) =>
            pendingTrade.trade_group_id
              ? t.trade_group_id === pendingTrade.trade_group_id
              : t.pairs === pendingTrade.pairs
          );
          setSelectedTrade(matched || updated[0]);
        }
        onPendingTradeConsumed?.();
      };
      open();
    }, [pendingTrade]);

    // Validate on data change
    React.useEffect(() => {
      if (portfolioData) {
        validatePortfolioData(portfolioData);
      } else {
        setDataErrors({});
      }
    }, [portfolioData, trades]); // Re-validate when trades (DB) or portfolioData (API) changes

    // Save API trades to database
    const handleSaveToDatabase = async () => {
      if (!selectedAccount) {
        alert('No CEX account selected');
        return;
      }

      setIsSavingToDb(true);
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          alert('Not authenticated. Please login again.');
          return;
        }

        const apiTrades = portfolioData?.activeDeployments || [];

        if (apiTrades.length === 0) {
          alert('No trades to save');
          return;
        }

        // Use -1 for All Accounts so backend maps each trade to its own cex_account_id
        const syncAccountId = selectedAccount.id;
        const response = await fetch(`${API_BASE_URL}/api/cex-accounts/${syncAccountId}/sync-trades`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            trades: apiTrades
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to save trades');
        }

        const data = await response.json();
        if (data.success) {
          // Refresh trades list
          await fetchTrades();

          // Refresh metrics via fast-mode sync so portfolioData reflects newly saved trades
          const isAllAccounts = selectedAccount.id === ALL_ACCOUNTS_ID;
          const fastUrl = isAllAccounts
            ? `${API_BASE_URL}/api/cex-accounts/sync-all?fast_mode=1`
            : `${API_BASE_URL}/api/cex-accounts/${selectedAccount.id}/sync?fast_mode=1`;
          try {
            const metricsRes = await fetch(fastUrl, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
            });
            if (metricsRes.ok) {
              const metricsData = await metricsRes.json();
              if (metricsData.success) setPortfolioData(metricsData.data);
            }
          } catch (_) {
            // Non-fatal – metrics will update on next manual sync
          }
        } else {
          throw new Error(data.message || 'Failed to save trades');
        }

      } catch (error: any) {
        console.error('Failed to save trades:', error);
        alert('Failed to save trades to database. Please try again.');
      } finally {
        setIsSavingToDb(false);
      }
    };

    const handleResync = async (showToast: boolean = true, fastMode: boolean = false) => {
      setIsSyncing(true);
      setSyncError('');

      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setSyncError('Authentication required. Please login again.');
          return;
        }

        let url = '';
        if (selectedWalletAccount) {
          url = `${API_BASE_URL}/api/wallet-accounts/${selectedWalletAccount.id}/sync`;
        } else {
          const isAllAccounts = !selectedAccount || selectedAccount.id === ALL_ACCOUNTS_ID;
          url = isAllAccounts
            ? `${API_BASE_URL}/api/cex-accounts/sync-all`
            : `${API_BASE_URL}/api/cex-accounts/${selectedAccount.id}/sync`;
        }

        // Add fast_mode parameter if requested
        if (fastMode) {
          url += (url.includes('?') ? '&' : '?') + 'fast_mode=1';
        }

        // Step 1: Sync account from exchange API
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to sync account');
        }

        const data = await response.json();
        if (data.success) {
          setPortfolioData(data.data);
          setSyncError('');
          
          if (selectedWalletAccount) {
            setLastSyncedAccountId(`wallet-${selectedWalletAccount.id}`);
          } else {
            const currentCexId = selectedAccount ? selectedAccount.id : ALL_ACCOUNTS_ID;
            if (currentCexId === ALL_ACCOUNTS_ID) {
              setLastSyncedAccountId('cex-all');
            } else {
              setLastSyncedAccountId(`cex-${currentCexId}`);
            }
          }

          // Step 2: Auto-save trades to database (works for CEX only, skipped for Wallet since Wallet sync runs auto-save in backend)
          if (!selectedWalletAccount && !fastMode && data.data.activeDeployments && data.data.activeDeployments.length > 0) {
            try {
              const isAllAccounts = !selectedAccount || selectedAccount.id === ALL_ACCOUNTS_ID;
              const syncAccountId = isAllAccounts ? ALL_ACCOUNTS_ID : selectedAccount?.id;
              const syncTradesResponse = await fetch(`${API_BASE_URL}/api/cex-accounts/${syncAccountId}/sync-trades`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Accept': 'application/json',
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  trades: data.data.activeDeployments
                }),
              });

              if (syncTradesResponse.ok) {
                const syncData = await syncTradesResponse.json();
                console.log(`Trades synced: ${syncData.data?.saved ?? 0} new, ${syncData.data?.updated ?? 0} updated`);

                // After saving, do a fast-mode re-sync to refresh metrics from newly saved DB trades
                const fastUrl = isAllAccounts
                  ? `${API_BASE_URL}/api/cex-accounts/sync-all?fast_mode=1`
                  : `${API_BASE_URL}/api/cex-accounts/${selectedAccount?.id}/sync?fast_mode=1`;
                const metricsRes = await fetch(fastUrl, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
                });
                if (metricsRes.ok) {
                  const metricsData = await metricsRes.json();
                  if (metricsData.success) setPortfolioData(metricsData.data);
                }
              }
            } catch (syncError) {
              console.error('Failed to auto-sync trades to database:', syncError);
            }
          }

          // Step 3: Fetch trades from database
          await fetchTrades();
        } else {
          throw new Error(data.message || 'Sync failed');
        }
      } catch (e: any) {
        if (selectedAccount || selectedWalletAccount) {
          setSyncError(e.message || 'Failed to sync. Please check your credentials/address.');
        } else {
          console.warn('Failed to sync manual trades:', e);
        }
      } finally {
        setIsSyncing(false);
      }
    };

    const realDbTrades = (trades || []).filter((t: any) => t._source !== 'mock');
    const hasRealTrades = realDbTrades.length > 0 || (portfolioData?.activeDeployments && portfolioData.activeDeployments.length > 0);

    const balance = portfolioData?.totalBalance || "$0.00";
    const pnl = (() => {
      if (portfolioData?.netPnl && portfolioData.netPnl !== "$0.00") {
        return portfolioData.netPnl;
      }
      if (realDbTrades.length > 0) {
        const totalPnl = realDbTrades.reduce((sum, t) => sum + (parseFloat(t.pnl_amount) || 0), 0);
        return formatCurrency(totalPnl);
      }
      return "$0.00";
    })();
    const pnlPercent = (() => {
      if (portfolioData?.netPnlPercent && portfolioData.netPnlPercent !== "0%") {
        return portfolioData.netPnlPercent;
      }
      return "0%";
    })();
    const pnlPositive = (() => {
      if (portfolioData?.netPnl && portfolioData.netPnl !== "$0.00") {
        return portfolioData.netPnlPositive ?? true;
      }
      if (realDbTrades.length > 0) {
        const totalPnl = realDbTrades.reduce((sum, t) => sum + (parseFloat(t.pnl_amount) || 0), 0);
        return totalPnl >= 0;
      }
      return true;
    })();
    const winRate = (() => {
      if (portfolioData?.tradeWinRate && portfolioData.tradeWinRate !== "0%") {
        return portfolioData.tradeWinRate;
      }
      if (realDbTrades.length > 0) {
        const wins = realDbTrades.filter((t: any) => t.status === 'WIN').length;
        return `${Math.round((wins / realDbTrades.length) * 100)}%`;
      }
      return "0%";
    })();
    const lossRate = (() => {
      if (!hasRealTrades) return 0;
      return Math.max(0, 100 - (parseFloat(winRate) || 0));
    })();

    // Combine trades from database and activeDeployments
    // Priority: database trades (with manual fields) > activeDeployments (API data)
    const combinedTrades = React.useMemo(() => {
      const dbTrades = trades || [];
      const apiTrades = portfolioData?.activeDeployments || [];

      // Convert activeDeployments to trade format
      const apiTradesFormatted = apiTrades.map((deployment: any) => ({
        id: deployment.id,
        pairs: deployment.asset,
        direction: deployment.type.includes('LONG') ? 'LONG' : deployment.type.includes('SHORT') ? 'SHORT' : 'BUY',
        entry_price: deployment.entry !== 'N/A' ? parseFloat(deployment.entry.replace('$', '')) : 0,
        exit_price: null,
        position_size: deployment.size,
        pnl_amount: deployment.pnl.replace('+', '').replace('$', ''),
        pnl_percentage: deployment.percent !== '-' ? deployment.percent : null,
        status: deployment.positive ? 'WIN' : 'LOSS',
        trade_date: new Date().toISOString(),
        leverage: deployment.type.match(/\d+x/) ? parseFloat(deployment.type.match(/\d+x/)?.[0] || '1') : null,
        // No manual fields from API
        session: null,
        primary_setup_type: null,
        remarks: null,
        _source: 'api', // Mark as API source
        cex_account_id: deployment.cex_account_id
      }));

      // If we have database trades, use them; otherwise use API trades
      if (dbTrades.length > 0) {
        return dbTrades;
      }
      if (apiTradesFormatted.length > 0) {
        return apiTradesFormatted;
      }

      // Inject sample mock trade so the tutorial has elements to highlight, edit, and expand
      return [{
        id: 99999,
        pairs: 'BTCUSDT',
        direction: 'LONG',
        entry_price: '64500.00',
        exit_price: '68200.00',
        position_size: '0.25',
        pnl_amount: '925.00',
        pnl_percentage: '5.74',
        status: 'WIN',
        trade_date: new Date().toISOString(),
        leverage: 10,
        session: 'US Session',
        primary_setup_type: 'Breakout',
        remarks: 'Sample trade for tutorial tour',
        cex_account: {
          id: 99999,
          cex_name: 'binance',
          account_label: 'Virtual Wallet'
        },
        _source: 'mock'
      }];
    }, [trades, portfolioData]);

    // Count trades for each CEX/manual filter
    const cexCounts = React.useMemo(() => {
      const counts: Record<string, number> = { All: combinedTrades.length };

      let manualCount = 0;
      combinedTrades.forEach(t => {
        if (t.cex_account_id && t.cex_account_id > 0) {
          const idStr = t.cex_account_id.toString();
          counts[idStr] = (counts[idStr] || 0) + 1;
        } else if (t.cex_account?.id) {
          const idStr = t.cex_account.id.toString();
          counts[idStr] = (counts[idStr] || 0) + 1;
        } else {
          manualCount++;
        }
      });

      counts['Manual'] = manualCount;
      return counts;
    }, [combinedTrades]);

    // Filter trades
    const filteredTrades = combinedTrades.filter((t: any) => {
      const matchSide = filterSide === 'All' || t.direction === filterSide.toUpperCase();
      const matchStatus = filterStatus === 'All' || t.status === filterStatus.toUpperCase();

      let matchCex = true;
      if (!filterCexIds.includes('All')) {
        const isManualSelected = filterCexIds.includes('Manual');
        const selectedAccIds = filterCexIds.filter(id => id !== 'Manual').map(id => parseInt(id));
        const isTradeManual = !t.cex_account_id && !t.cex_account;

        if (isTradeManual) {
          matchCex = isManualSelected;
        } else {
          const tradeCexId = t.cex_account_id || t.cex_account?.id || 0;
          matchCex = selectedAccIds.includes(tradeCexId);
        }
      }

      return matchSide && matchStatus && matchCex;
    });

    const totalPages = Math.ceil(filteredTrades.length / TRADES_PER_PAGE);
    const paginatedTrades = filteredTrades.slice(
      (currentPage - 1) * TRADES_PER_PAGE,
      currentPage * TRADES_PER_PAGE
    );

    return (
      <div className="min-h-screen bg-black text-white flex">
        <Sidebar plan={formData.plan} />

        <main className="flex-1 bg-[#0A0A0A] min-h-screen relative pt-16 md:pt-0 overflow-x-hidden">
          <TopBar
            title="Trades"
            name={formData.name}
            showAccountSwitcher={true}
            showResync={true}
            isSyncing={isSyncing}
            onResync={() => handleResync(true)}
          />
          <div className="p-3 md:p-8 max-w-[1600px] mx-auto w-full">
            {/* Sync Error Alert */}
            {syncError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-900/20 border border-red-900/50 rounded-2xl p-4 flex items-center gap-3 mb-6"
              >
                <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-red-500">{syncError}</p>
                  <p className="text-xs text-neutral-400 mt-1">Try re-syncing or check your API settings.</p>
                </div>
                <button
                  onClick={() => setSyncError('')}
                  className="text-neutral-500 hover:text-white transition-colors"
                >
                  ×
                </button>
              </motion.div>
            )}

            {/* Empty State Warning */}
            {!portfolioData && !isSyncing && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-yellow-900/20 border border-yellow-900/50 rounded-2xl p-4 flex items-center gap-3 mb-6"
              >
                <AlertCircle size={20} className="text-yellow-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-yellow-500">No portfolio data found</p>
                  <p className="text-xs text-neutral-400 mt-1">Click "Re-sync" to fetch your latest trades from {selectedAccount?.cex_display_name || 'your exchange'}.</p>
                </div>
              </motion.div>
            )}

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4 mb-6 md:mb-8">
            <div className="flex flex-col">
              <ErrorBadge message={dataErrors.netPnl} />
              <StatCard
                title="NET PNL" value={pnl} percent={pnlPercent} positive={pnlPositive}
                chart={
                  <PnlLineChart pnlPositive={pnlPositive} />
                }
              />
            </div>
            <div className="flex flex-col">
              <ErrorBadge message={dataErrors.winRate} />
              <StatCard
                title="WIN RATE %" value={winRate}
                chart={
                  <AnimatedCircle winRate={winRate} />
                }
              />
            </div>
            <div className="flex flex-col">
              <ErrorBadge message={dataErrors.lossRate} />
              <StatCard
                title="LOSS RATE %"
                value={`${lossRate.toFixed(1)}%`}
                chart={<AnimatedLossCircle lossRate={lossRate} />}
              />
            </div>
            <div className="flex flex-col">
              <ErrorBadge message={dataErrors.profitFactor} />
              <StatCard
                title="PROFIT FACTOR"
                value={<ProfitFactorNumber target={getProfitFactor()} />}
                sub={`Health: ${getProfitFactor() > 1 ? 'Optimal' : 'Needs Improvement'}`}
              />
            </div>
          </div>

            {/* Filters Area */}
            <div className="flex flex-col gap-3 mb-6">
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-3 w-full">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-1.5 w-full sm:w-auto">
                  <span className="text-[11px] text-neutral-500 font-bold">Exchange:</span>
                  <div className="w-full sm:w-auto">
                    <FilterDropdown
                      label="Exchange"
                      value={filterCexIds}
                      options={cexFilterOptions.map(opt => ({
                        ...opt,
                        count: cexCounts[opt.id] || 0
                      }))}
                      onChange={(v) => { setFilterCexIds(v); setCurrentPage(1); }}
                      multiple={true}
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-1.5 w-full sm:w-auto">
                  <span className="text-[11px] text-neutral-500 font-bold">Date:</span>
                  <div className="w-full sm:w-auto">
                    <FilterDropdown
                      label="Date"
                      value={filterDate}
                      options={[
                        { id: 'All Time', label: 'All Time' },
                        { id: 'Today', label: 'Today' },
                        { id: 'This Week', label: 'This Week' },
                        { id: 'This Month', label: 'This Month' }
                      ]}
                      onChange={(v) => { setFilterDate(v); setCurrentPage(1); }}
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-1.5 w-full sm:w-auto">
                  <span className="text-[11px] text-neutral-500 font-bold">Side:</span>
                  <div className="w-full sm:w-auto">
                    <FilterDropdown
                      label="Side"
                      value={filterSide}
                      options={[
                        { id: 'All', label: 'All Sides' },
                        { id: 'Long', label: 'Long', color: '#3b82f6' },
                        { id: 'Short', label: 'Short', color: '#f59e0b' }
                      ]}
                      onChange={(v) => { setFilterSide(v); setCurrentPage(1); }}
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-1.5 w-full sm:w-auto">
                  <span className="text-[11px] text-neutral-500 font-bold">Status:</span>
                  <div className="w-full sm:w-auto">
                    <FilterDropdown
                      label="Status"
                      value={filterStatus}
                      options={[
                        { id: 'All', label: 'All Statuses' },
                        { id: 'Win', label: 'Wins', color: '#10b981' },
                        { id: 'Loss', label: 'Losses', color: '#ef4444' }
                      ]}
                      onChange={(v) => { setFilterStatus(v); setCurrentPage(1); }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between min-h-[28px] flex-wrap gap-y-2 gap-x-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <AnimatePresence>
                    {!filterCexIds.includes('All') && (
                      <motion.div key="chip-exchange" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.15 }}
                        className="flex items-center gap-1 px-2.5 py-1 bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-bold rounded-full">
                        Exchange: {filterCexIds.length} selected
                        <button onClick={() => { setFilterCexIds(['All']); setCurrentPage(1); }} className="ml-0.5 hover:text-white transition-colors">×</button>
                      </motion.div>
                    )}
                    {filterDate !== 'All Time' && (
                      <motion.div key="chip-date" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.15 }}
                        className="flex items-center gap-1 px-2.5 py-1 bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-bold rounded-full">
                        Date: {filterDate}
                        <button onClick={() => { setFilterDate('All Time'); setCurrentPage(1); }} className="ml-0.5 hover:text-white transition-colors">×</button>
                      </motion.div>
                    )}
                    {filterSide !== 'All' && (
                      <motion.div key="chip-side" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.15 }}
                        className="flex items-center gap-1 px-2.5 py-1 bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-bold rounded-full">
                        Side: {filterSide}
                        <button onClick={() => { setFilterSide('All'); setCurrentPage(1); }} className="ml-0.5 hover:text-white transition-colors">×</button>
                      </motion.div>
                    )}
                    {filterStatus !== 'All' && (
                      <motion.div key="chip-status" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.15 }}
                        className="flex items-center gap-1 px-2.5 py-1 bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-bold rounded-full">
                        Status: {filterStatus}
                        <button onClick={() => { setFilterStatus('All'); setCurrentPage(1); }} className="ml-0.5 hover:text-white transition-colors">×</button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider">Total: {filteredTrades.length} trades</span>
                  <span className="text-neutral-800">|</span>
                  <button
                    onClick={() => {
                      setFilterCexIds(['All']);
                      setFilterDate('All Time');
                      setFilterSide('All');
                      setFilterStatus('All');
                      setCurrentPage(1);
                    }}
                    className="text-[10px] font-black uppercase tracking-widest text-neutral-500 hover:text-white transition-colors"
                  >
                    Reset All Filters
                  </button>
                </div>
              </div>
            </div>


            {/* Trades Table */}
            {dataErrors.tradesTable && (
              <div className="mb-4">
                <ErrorBadge message={dataErrors.tradesTable} />
              </div>
            )}

            {/* Info: Trades from API */}
            {combinedTrades.length > 0 && combinedTrades[0]?._source === 'api' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-blue-900/20 border border-blue-900/50 rounded-2xl p-4 flex items-center gap-3 mb-4"
              >
                <AlertCircle size={20} className="text-blue-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-blue-500">Showing live data from API</p>
                  <p className="text-xs text-neutral-400 mt-1">
                    These trades are not yet saved to database. Click "Save to Database" to enable editing and manual fields.
                  </p>
                </div>
                <button
                  onClick={handleSaveToDatabase}
                  disabled={isSavingToDb}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {isSavingToDb ? 'Saving...' : 'Save to Database'}
                </button>
              </motion.div>
            )}

            <div className="overflow-x-auto rounded-2xl -mx-3 md:mx-0 scrollbar-hide">
            <div data-tour="trades-table" className="bg-[#111] border border-neutral-800 rounded-2xl overflow-hidden min-w-[1000px]">
              <table className="w-full text-left border-collapse">
                <thead className="bg-[#0A0A0A] text-[10px] uppercase font-black text-neutral-500 tracking-widest border-b border-neutral-800">
                  <tr>
                    <th className="px-3 py-4">Status</th>
                    <th className="px-3 py-4">Exchange / Chain</th>
                    <th className="px-3 py-4">Date / Time</th>
                    <th className="px-3 py-4">Asset</th>
                    <th className="px-3 py-4 text-right">Entry / Exit</th>
                    <th className="px-3 py-4 text-right">Size</th>
                    <th className="px-3 py-4 text-right">Return</th>
                    <th className="px-3 py-4">Side</th>
                    <th className="px-3 py-4">Setup</th>
                    <th className="px-3 py-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800/50">
                  {filteredTrades.length > 0 ? (
                    paginatedTrades.map((trade: any) => (
                      <tr
                        key={trade.id}
                        className="hover:bg-neutral-800/30 transition-all group"
                      >
                        <td className="px-3 py-4">
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded ${trade.status === 'WIN' ? 'bg-green-500/10 text-green-500' : trade.status === 'LOSS' ? 'bg-red-500/10 text-red-500' : 'bg-neutral-800 text-neutral-400'}`}>
                            {trade.status}
                          </span>
                        </td>
                        <td className="px-3 py-4">
                          {trade.cex_account ? (
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded bg-neutral-800 flex items-center justify-center p-1">
                                <img
                                  src={getExchangeInfo(trade.cex_account.cex_name)?.icon}
                                  alt={trade.cex_account.cex_name}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                              <span className="text-[10px] font-bold text-neutral-400 uppercase">
                                {trade.cex_account.cex_name}
                              </span>
                            </div>
                          ) : trade.wallet_account ? (
                            <div className="flex items-center gap-1.5">
                              <Wallet size={12} className="text-purple-400" />
                              <span className="text-[10px] font-bold text-purple-400 uppercase">
                                {trade.wallet_account.network_display_name || trade.wallet_account.network_name || 'ON-CHAIN'}
                              </span>
                            </div>
                          ) : (
                            <span className="text-[10px] text-neutral-600 font-bold">MANUAL</span>
                          )}
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-xs font-bold text-white font-mono">
                            {new Date(trade.trade_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                          <div className="text-[10px] text-neutral-500 mt-0.5 font-mono">
                            {new Date(trade.trade_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-xs font-black text-white">{trade.pairs}</td>
                        <td className="px-3 py-4 text-right">
                          <div className="text-xs font-bold text-neutral-300 font-mono">
                            {trade.entry_price != null && parseFloat(trade.entry_price) > 0
                              ? `$${parseFloat(trade.entry_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`
                              : <span className="text-neutral-600">—</span>}
                          </div>
                          <div className="text-[10px] text-neutral-500 mt-0.5 font-mono">
                            {trade.exit_price != null && parseFloat(trade.exit_price) > 0
                              ? `$${parseFloat(trade.exit_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`
                              : <span className="text-neutral-700">—</span>}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-right text-xs font-bold text-neutral-400 font-mono">
                          {trade.position_size
                            ? `${parseFloat(trade.position_size).toLocaleString('en-US', { maximumFractionDigits: 8 })} ${trade.pairs?.split('/')?.[0] ?? trade.pairs?.replace(/USDT|USDC|BTC|ETH|BNB$/i, '') ?? ''}`
                            : '—'}
                        </td>
                        <td className="px-3 py-4 text-right">
                          {trade.pnl_amount != null && parseFloat(trade.pnl_amount) !== 0 ? (
                            <>
                              <div className={`text-xs font-black font-mono ${trade.status === 'WIN' ? 'text-green-500' : 'text-red-500'}`}>
                                {formatCurrency(trade.pnl_amount)}
                              </div>
                              <div className={`text-[9px] font-black font-mono mt-0.5 ${trade.status === 'WIN' ? 'text-green-600/60' : 'text-red-600/60'}`}>
                                {trade.pnl_percentage ? `${parseFloat(trade.pnl_percentage) >= 0 ? '+' : ''}${trade.pnl_percentage}%` : '—'}
                              </div>
                            </>
                          ) : (
                            <span className="text-neutral-600 font-mono text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-4">
                          <span className={`text-[9px] font-black tracking-widest uppercase ${trade.direction === 'LONG' ? 'text-blue-500' : 'text-amber-500'}`}>
                            {trade.direction}
                          </span>
                        </td>
                        <td className="px-3 py-4 text-[10px] font-bold text-neutral-500 uppercase">
                          {trade.primary_setup_type || 'Auto Sync'}
                        </td>
                        <td className="px-3 py-4 text-right">
                          <div className="flex items-center justify-end gap-3">
                            {trade.remarks && <MessageSquare size={14} className="text-neutral-600" />}
                            {trade._source === 'api' ? (
                              <button
                                data-tour="trades-edit"
                                disabled
                                className="p-1.5 rounded-lg opacity-40 cursor-not-allowed hover:bg-neutral-800/40"
                                title="Trade from API - Sync account to save to database first"
                              >
                                <Edit size={14} className="text-neutral-500" />
                              </button>
                            ) : (
                              <>
                                <button
                                  data-tour="trades-edit"
                                  onClick={(e) => { e.stopPropagation(); setEditingTrade(trade); }}
                                  className="p-1.5 hover:bg-neutral-800 rounded-lg transition-colors text-neutral-400 hover:text-white"
                                  title="Edit trade details"
                                >
                                  <Edit size={14} />
                                </button>
                                {confirmDeleteTradeId === trade.id ? (
                                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                    <button onClick={() => handleDeleteTrade(trade.id)} className="text-[10px] px-1.5 py-0.5 bg-red-600 hover:bg-red-500 text-white rounded font-bold transition-colors">Yes</button>
                                    <button onClick={() => setConfirmDeleteTradeId(null)} className="text-[10px] px-1.5 py-0.5 bg-neutral-700 hover:bg-neutral-600 text-white rounded font-bold transition-colors">No</button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteTradeId(trade.id); }}
                                    disabled={deletingTradeId === trade.id}
                                    className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors text-neutral-600 hover:text-red-400"
                                    title="Delete trade"
                                  >
                                    {deletingTradeId === trade.id
                                      ? <RefreshCw size={13} className="animate-spin" />
                                      : <Trash2 size={13} />
                                    }
                                  </button>
                                )}
                              </>
                            )}
                            <button
                              data-tour="trades-expand"
                              onClick={() => setSelectedTrade(trade)}
                              className="p-1.5 hover:bg-neutral-800 rounded-lg transition-colors text-neutral-400 hover:text-white"
                            >
                              <ChevronRight size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="px-3 py-12 text-center">
                        <div className="flex flex-col items-center gap-3 text-neutral-600">
                          <AlertCircle size={32} className="opacity-20" />
                          <p className="text-sm font-bold">
                            {isLoadingTrades ? 'Loading trades...' : 'No trades found'}
                          </p>
                          <p className="text-xs">
                            {isLoadingTrades ? 'Please wait...' : 'Sync your exchange or adjust filters to see trades'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </div>
            {filteredTrades.length > 0 && (
              <div className="flex items-center justify-between px-2 py-4">
                <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider">
                  Showing {(currentPage - 1) * TRADES_PER_PAGE + 1}–{Math.min(currentPage * TRADES_PER_PAGE, filteredTrades.length)} of {filteredTrades.length} trades
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a1a1a] border border-neutral-800 text-neutral-400 hover:text-white transition-all ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {totalPages <= 5
                    ? Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-8 h-8 rounded-lg text-xs font-bold flex items-center justify-center transition-all ${currentPage === page ? 'bg-red-600 text-white' : 'bg-[#1a1a1a] border border-neutral-800 text-neutral-400 hover:text-white'}`}
                        >{page}</button>
                      ))
                    : (() => {
                        const pages: (number | 'e')[] = [];
                        if (currentPage <= 3) {
                          pages.push(1, 2, 3, 'e', totalPages);
                        } else if (currentPage >= totalPages - 2) {
                          pages.push(1, 'e', totalPages - 2, totalPages - 1, totalPages);
                        } else {
                          pages.push(1, 'e', currentPage - 1, currentPage, currentPage + 1, 'e', totalPages);
                        }
                        return pages.map((p, i) =>
                          p === 'e'
                            ? <span key={`e${i}`} className="w-8 h-8 flex items-center justify-center text-neutral-600 text-xs">...</span>
                            : <button key={`${p}-${i}`} onClick={() => setCurrentPage(p as number)}
                                className={`w-8 h-8 rounded-lg text-xs font-bold flex items-center justify-center transition-all ${currentPage === p ? 'bg-red-600 text-white' : 'bg-[#1a1a1a] border border-neutral-800 text-neutral-400 hover:text-white'}`}
                              >{p}</button>
                        );
                      })()
                  }
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a1a1a] border border-neutral-800 text-neutral-400 hover:text-white transition-all ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Trade Detail Drawer */}
          <AnimatePresence>
            {selectedTrade && (
              <>
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  onClick={() => setSelectedTrade(null)}
                  className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                />
                <motion.div
                  initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                  className="fixed right-0 top-0 bottom-0 w-full h-full bg-[#0A0A0A] border-l border-neutral-800 z-50 shadow-2xl flex flex-col"
                >
                  {/* TradeZella-Style Header Bar */}
                  <div className="px-4 md:px-8 py-3 md:py-5 border-b border-neutral-800 bg-[#0F0F0F]">
                    {/* Mobile: Compact header with Prev/Next */}
                    <div className="flex md:hidden items-center justify-between gap-2 mb-3">
                      <button 
                        onClick={() => {
                          const currentIndex = filteredTrades.findIndex((t: any) => t.id === selectedTrade.id);
                          if (currentIndex > 0) {
                            setSelectedTrade(filteredTrades[currentIndex - 1]);
                          }
                        }}
                        disabled={filteredTrades.findIndex((t: any) => t.id === selectedTrade.id) === 0}
                        className="p-2 hover:bg-neutral-800 rounded-lg transition-colors text-neutral-400 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Previous trade"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      
                      <div className="flex-1 flex items-center justify-center gap-2">
                        <h2 className="text-lg font-black text-white tracking-tight">
                          {selectedTrade.pairs}
                        </h2>
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded tracking-wide uppercase ${
                          selectedTrade.status === 'WIN' ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'
                        }`}>
                          {selectedTrade.status}
                        </span>
                        <span className={`text-[8px] font-black tracking-widest uppercase px-2 py-0.5 rounded ${
                          selectedTrade.direction === 'LONG' ? 'bg-blue-500/10 text-blue-500 border border-blue-500/20' : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                        }`}>
                          {selectedTrade.direction}
                        </span>
                      </div>

                      <button 
                        onClick={() => {
                          const currentIndex = filteredTrades.findIndex((t: any) => t.id === selectedTrade.id);
                          if (currentIndex < filteredTrades.length - 1) {
                            setSelectedTrade(filteredTrades[currentIndex + 1]);
                          }
                        }}
                        disabled={filteredTrades.findIndex((t: any) => t.id === selectedTrade.id) === filteredTrades.length - 1}
                        className="p-2 hover:bg-neutral-800 rounded-lg transition-colors text-neutral-400 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Next trade"
                      >
                        <ChevronRight size={20} />
                      </button>

                      <button 
                        onClick={() => setSelectedTrade(null)} 
                        className="p-2 hover:bg-neutral-800 rounded-lg transition-colors text-neutral-400 hover:text-white"
                      >
                        <X size={20} />
                      </button>
                    </div>

                    <div className="flex md:hidden justify-center">
                      <p className="text-[10px] text-neutral-500 font-bold tracking-wide uppercase font-mono">
                        {new Date(selectedTrade.trade_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>

                    {/* Desktop: Original layout */}
                    <div className="hidden md:flex flex-wrap gap-2 justify-between items-center">
                      <div className="flex items-center gap-4">
                        <button 
                          onClick={() => setSelectedTrade(null)} 
                          className="p-2 hover:bg-neutral-800 rounded-xl transition-colors text-neutral-400 hover:text-white"
                        >
                          <ChevronLeft size={20} />
                        </button>
                        <div>
                          <div className="flex items-center gap-3">
                            <h2 className="text-xl font-black text-white tracking-tight flex items-center gap-2">
                              {selectedTrade.pairs}
                            </h2>
                            <span className={`text-[10px] font-black px-2 py-0.5 rounded tracking-wide uppercase ${
                              selectedTrade.status === 'WIN' ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'
                            }`}>
                              {selectedTrade.status}
                            </span>
                            <span className={`text-[9px] font-black tracking-widest uppercase px-2 py-0.5 rounded ${
                              selectedTrade.direction === 'LONG' ? 'bg-blue-500/10 text-blue-500 border border-blue-500/20' : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                            }`}>
                              {selectedTrade.direction}
                            </span>
                          </div>
                          <p className="text-[11px] text-neutral-500 font-bold mt-1 tracking-wide uppercase font-mono">
                            {new Date(selectedTrade.trade_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button 
                          onClick={() => handleToggleReview(selectedTrade.id)}
                          className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg transition-all text-[11px] font-bold ${
                            reviewedTrades[selectedTrade.id]
                              ? 'bg-green-950/30 border-green-500/50 text-green-400 font-black'
                              : 'bg-neutral-900 border-neutral-800 text-neutral-300 hover:bg-neutral-800'
                          }`}
                        >
                          <Check size={14} className={reviewedTrades[selectedTrade.id] ? 'text-green-400 animate-pulse' : 'text-neutral-500'} />
                          <span>{reviewedTrades[selectedTrade.id] ? 'Reviewed' : 'Mark as reviewed'}</span>
                        </button>
                        <button 
                          onClick={() => setReplayTrade(selectedTrade)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-neutral-900 border border-neutral-800 rounded-lg hover:bg-neutral-800 text-[11px] font-bold text-neutral-300 transition-all"
                        >
                          <Play size={12} className="text-blue-500 fill-blue-500 animate-pulse" />
                          <span>Replay</span>
                        </button>
                        <button 
                          onClick={() => handleShareTrade(selectedTrade.pairs, parseFloat(selectedTrade.pnl_amount))}
                          className="flex items-center gap-2 px-3 py-1.5 bg-neutral-900 border border-neutral-800 rounded-lg hover:bg-neutral-800 text-[11px] font-bold text-neutral-300 transition-all"
                        >
                          <Share2 size={12} className="text-purple-500" />
                          <span>Share</span>
                        </button>
                        <button 
                          onClick={() => {
                            if (selectedTrade._source === 'api') {
                              alert('This trade is from API and not yet saved to database. Please sync your account first to save trades to database.');
                            } else {
                              setEditingTrade(selectedTrade);
                            }
                          }}
                          className="p-2 hover:bg-neutral-800 rounded-xl transition-colors text-neutral-400 hover:text-white"
                          title="Edit trade details"
                        >
                          <Settings size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Mobile: Action buttons (stacked) */}
                    <div className="flex md:hidden flex-col gap-2 mt-3">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleToggleReview(selectedTrade.id)}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 border rounded-lg transition-all text-[11px] font-bold min-h-[48px] ${
                            reviewedTrades[selectedTrade.id]
                              ? 'bg-green-950/30 border-green-500/50 text-green-400 font-black'
                              : 'bg-neutral-900 border-neutral-800 text-neutral-300 active:bg-neutral-800'
                          }`}
                        >
                          <Check size={16} className={reviewedTrades[selectedTrade.id] ? 'text-green-400 animate-pulse' : 'text-neutral-500'} />
                          <span className="whitespace-nowrap">{reviewedTrades[selectedTrade.id] ? 'Reviewed' : 'Mark reviewed'}</span>
                        </button>
                        <button 
                          onClick={() => setReplayTrade(selectedTrade)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-neutral-900 border border-neutral-800 rounded-lg active:bg-neutral-800 text-[11px] font-bold text-neutral-300 transition-all min-h-[48px]"
                        >
                          <Play size={14} className="text-blue-500 fill-blue-500 animate-pulse" />
                          <span>Replay</span>
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleShareTrade(selectedTrade.pairs, parseFloat(selectedTrade.pnl_amount))}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-neutral-900 border border-neutral-800 rounded-lg active:bg-neutral-800 text-[11px] font-bold text-neutral-300 transition-all min-h-[48px]"
                        >
                          <Share2 size={14} className="text-purple-500" />
                          <span>Share</span>
                        </button>
                        <button 
                          onClick={() => {
                            if (selectedTrade._source === 'api') {
                              alert('This trade is from API and not yet saved to database. Please sync your account first to save trades to database.');
                            } else {
                              setEditingTrade(selectedTrade);
                            }
                          }}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-neutral-900 border border-neutral-800 rounded-lg active:bg-neutral-800 text-[11px] font-bold text-neutral-300 transition-all min-h-[48px]"
                          title="Edit trade details"
                        >
                          <Settings size={18} />
                          <span>Settings</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Main Split Body Layout */}
                  <div className="flex-1 w-full flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
                    
                    {/* Left Column: Stats & Setup details */}
                    <div className="w-full md:w-[380px] md:max-h-none border-b md:border-b-0 border-r-0 md:border-r border-neutral-800 bg-[#0F0F0F] flex flex-col overflow-hidden">
                      {/* Left Column Tabs */}
                      <div className="flex overflow-x-auto scrollbar-hide border-b border-neutral-800 p-2 gap-1 bg-[#0A0A0A]">
                        {['Stats', 'Strategy', 'Executions', 'Attachments'].map((tab) => (
                          <button
                            key={tab}
                            onClick={() => setLeftTab(tab as any)}
                            className={`flex-shrink-0 px-4 py-2.5 md:py-2 text-[11px] font-black uppercase tracking-wider rounded-lg transition-all min-h-[48px] md:min-h-0 ${
                              leftTab === tab 
                                ? 'bg-neutral-900 text-white shadow' 
                                : 'text-neutral-500 hover:text-neutral-300'
                            }`}
                          >
                            {tab}
                          </button>
                        ))}
                      </div>

                      {/* Left Tabs Content Area */}
                      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6" data-lenis-prevent>
                        {leftTab === 'Stats' && (
                          <div className="space-y-4 md:space-y-6">
                            {/* Net P&L Card with Left border */}
                            <div className={`p-4 md:p-5 bg-neutral-900/50 rounded-xl border border-neutral-800 border-l-4 ${
                              selectedTrade.status === 'WIN' ? 'border-l-green-500' : 'border-l-red-500'
                            }`}>
                              <p className="text-[10px] font-black uppercase text-neutral-500 tracking-widest mb-1.5">Net P&L</p>
                              <h3 className={`text-3xl font-black tracking-tight font-mono ${
                                selectedTrade.status === 'WIN' ? 'text-green-500' : 'text-red-500'
                              }`}>
                                {parseFloat(selectedTrade.pnl_amount) >= 0 ? '+' : ''}{formatCurrency(selectedTrade.pnl_amount)}
                              </h3>
                            </div>

                            {/* Stats Table list */}
                            <div className="space-y-3">
                              <h4 className="text-[10px] font-black uppercase text-neutral-500 tracking-wider">Trade Properties</h4>
                              <div className="bg-neutral-950/45 rounded-2xl border border-neutral-800/85 overflow-hidden">
                                <StatRow label="Side" value={selectedTrade.direction} valueColor={selectedTrade.direction === 'LONG' ? 'text-blue-500' : 'text-amber-500'} />
                                <StatRow 
                                  label="Account" 
                                  value={
                                    selectedTrade.cex_account ? (
                                      <span className="flex items-center gap-1.5">
                                        <img 
                                          src={getExchangeInfo(selectedTrade.cex_account.cex_name)?.icon} 
                                          alt={selectedTrade.cex_account.cex_name}
                                          className="w-3.5 h-3.5 object-contain"
                                        />
                                        <span className="uppercase">{selectedTrade.cex_account.cex_name}</span>
                                      </span>
                                    ) : 'MANUAL'
                                  } 
                                />
                                <StatRow 
                                  label="Avg Fill Price"
                                  value={
                                    (selectedTrade.entry_price ?? selectedTrade.exit_price) != null
                                      ? `$${parseFloat(selectedTrade.entry_price ?? selectedTrade.exit_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`
                                      : 'N/A'
                                  }
                                  fontMono
                                />
                                <StatRow 
                                  label="Entry Price"
                                  value={selectedTrade.entry_price != null
                                    ? `$${parseFloat(selectedTrade.entry_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`
                                    : 'N/A'
                                  }
                                  fontMono
                                />
                                <StatRow 
                                  label="Exit Price"
                                  value={selectedTrade.exit_price != null
                                    ? `$${parseFloat(selectedTrade.exit_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`
                                    : 'N/A'
                                  }
                                  fontMono
                                />
                                <StatRow label="Position Size" value={selectedTrade.position_size != null ? parseFloat(selectedTrade.position_size).toString() : '1'} fontMono />
                                <StatRow label="Commissions & Fees" value={selectedTrade.fee != null ? `$${parseFloat(selectedTrade.fee).toFixed(6)}` : '$0.000000'} fontMono />
                                <StatRow label="Net ROI" value={selectedTrade.pnl_percentage ? `${selectedTrade.pnl_percentage}%` : '-'} fontMono valueColor={selectedTrade.status === 'WIN' ? 'text-green-500' : 'text-red-500'} />
                                <StatRow label="Gross P&L" value={formatCurrency(selectedTrade.pnl_amount)} fontMono valueColor={selectedTrade.status === 'WIN' ? 'text-green-500' : 'text-red-500'} />
                                <StatRow 
                                  label="Adjusted Cost" 
                                  value={
                                    (selectedTrade.entry_price ?? selectedTrade.exit_price)
                                      ? `$${((selectedTrade.entry_price ?? selectedTrade.exit_price) * (parseFloat(selectedTrade.position_size) || 1)).toFixed(2)}`
                                      : 'N/A'
                                  }
                                  fontMono
                                />
                                <StatRow label="Setup Type" value={selectedTrade.primary_setup_type || (selectedTrade.is_manual ? 'Manual Trade' : 'Auto Sync')} />
                              </div>
                            </div>

                            {/* Extra info cards */}
                            {selectedTrade.pre_trade_confidence !== undefined && selectedTrade.pre_trade_confidence !== null && selectedTrade.pre_trade_confidence > 0 && (
                              <div className="bg-neutral-950/40 p-4 rounded-xl border border-neutral-800/80">
                                <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1.5">Pre-Trade Confidence</p>
                                <div className="flex items-center gap-3">
                                  <p className="text-xs text-white font-bold">{selectedTrade.pre_trade_confidence}/10</p>
                                  <div className="flex-1 h-2 bg-neutral-900 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-blue-500 rounded-full transition-all"
                                      style={{ width: `${(selectedTrade.pre_trade_confidence / 10) * 100}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {leftTab === 'Strategy' && (
                          <div className="space-y-4">
                            <h4 className="text-[10px] font-black uppercase text-neutral-500 tracking-wider">Setup Strategy</h4>
                            <div className="bg-neutral-900/30 p-4 md:p-5 rounded-xl md:rounded-2xl border border-neutral-800 space-y-3 md:space-y-4">
                              <div>
                                <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Primary Setup</p>
                                <p className="text-xs text-white font-bold uppercase tracking-wider">{selectedTrade.primary_setup_type || (selectedTrade._source === 'api' ? 'Auto Sourced (Sync)' : 'Manual Trade')}</p>
                              </div>
                              <div>
                                <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Timeframe Analysis</p>
                                <p className="text-xs text-white font-bold">{selectedTrade.timeframe_analysis || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Trading Session</p>
                                <p className="text-xs text-white font-bold uppercase tracking-wide">{selectedTrade.session || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Market Cap Class</p>
                                <p className="text-xs text-white font-bold uppercase">{selectedTrade.market_cap || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Key Technical Indicators</p>
                                <p className="text-xs text-neutral-300 leading-relaxed font-bold">{selectedTrade.key_indicators || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {leftTab === 'Executions' && (
                          <div className="space-y-4">
                            <h4 className="text-[10px] font-black uppercase text-neutral-500 tracking-wider">Execution Log</h4>
                            <div className="bg-neutral-900/30 p-4 md:p-5 rounded-xl md:rounded-2xl border border-neutral-800 space-y-3">
                              <div className="flex justify-between items-center text-xs">
                                <span className="text-neutral-500 font-bold">Filled Date</span>
                                <span className="text-neutral-300 font-mono">{new Date(selectedTrade.trade_date).toLocaleDateString()}</span>
                              </div>
                              <div className="flex justify-between items-center text-xs">
                                <span className="text-neutral-500 font-bold">Direction</span>
                                <span className={`font-bold uppercase ${selectedTrade.direction === 'LONG' ? 'text-blue-500' : 'text-amber-500'}`}>{selectedTrade.direction}</span>
                              </div>
                              <div className="flex justify-between items-center text-xs">
                                <span className="text-neutral-500 font-bold">Quantity</span>
                                <span className="text-neutral-300 font-mono">{selectedTrade.position_size || '1'}</span>
                              </div>
                              <div className="flex justify-between items-center text-xs">
                                <span className="text-neutral-500 font-bold">Avg Price</span>
                                <span className="text-neutral-300 font-mono">
                                  {selectedTrade.entry_price != null
                                    ? `$${parseFloat(selectedTrade.entry_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`
                                    : 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {leftTab === 'Attachments' && (
                          <div className="space-y-3 h-full overflow-y-auto" data-lenis-prevent>
                            {/* Header */}
                            <div className="flex items-center justify-between">
                              <h4 className="text-[10px] font-black uppercase text-neutral-500 tracking-wider">Visual Evidence</h4>
                              <div className="flex items-center gap-2">
                                {uploadingAttachment && (
                                  <span className="text-[9px] text-red-400 animate-pulse font-bold uppercase tracking-wider">Uploading...</span>
                                )}
                                {(tradeAttachments[selectedTrade?.id] || []).length > 0 && (
                                  <span className="text-[9px] text-neutral-600 font-mono">
                                    {(tradeAttachments[selectedTrade?.id] || []).length} file{(tradeAttachments[selectedTrade?.id] || []).length > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Hidden file input */}
                            <input
                              ref={attachmentInputRef}
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              onChange={handleAttachmentUpload}
                            />

                            {/* Uploaded image cards with CDN URL */}
                            {(tradeAttachments[selectedTrade?.id] || []).map((url, idx) => {
                              const copyKey = `${selectedTrade?.id}-${idx}`;
                              const wasCopied = copiedUrlIdx === copyKey;
                              const shortUrl = url.length > 48 ? '...' + url.slice(-45) : url;
                              return (
                                <div key={idx} className="rounded-2xl border border-neutral-800 overflow-hidden bg-neutral-900/40 group">
                                  {/* Image preview */}
                                  <div className="relative aspect-video overflow-hidden">
                                    <img src={url} alt={`Attachment ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
                                    {/* Overlay actions */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end justify-between p-3">
                                      <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-white bg-white/10 backdrop-blur-sm border border-white/20 px-2.5 py-1.5 rounded-lg hover:bg-white/20 transition-colors"
                                      >
                                        <Link size={9} />
                                        Open Full
                                      </a>
                                      <button
                                        onClick={() => setTradeAttachments(prev => ({
                                          ...prev,
                                          [selectedTrade.id]: (prev[selectedTrade.id] || []).filter((_, i) => i !== idx),
                                        }))}
                                        className="w-6 h-6 bg-red-500/20 border border-red-500/30 rounded-full flex items-center justify-center text-red-400 hover:bg-red-500 hover:text-white transition-all"
                                      >
                                        <X size={10} />
                                      </button>
                                    </div>
                                  </div>

                                  {/* CDN URL bar */}
                                  <div className="px-3 py-2.5 flex items-center gap-2 border-t border-neutral-800/80">
                                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                      <div className="flex-shrink-0 flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-1.5 py-0.5">
                                        <div className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                                        <span className="text-[8px] font-black uppercase tracking-widest text-emerald-400">CDN</span>
                                      </div>
                                      <span className="text-[9px] font-mono text-neutral-500 truncate" title={url}>{shortUrl}</span>
                                    </div>
                                    <button
                                      onClick={() => handleCopyUrl(url, copyKey)}
                                      className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-wider transition-all ${
                                        wasCopied
                                          ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
                                          : 'bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-neutral-400 hover:text-white'
                                      }`}
                                    >
                                      {wasCopied ? <Check size={9} /> : <Copy size={9} />}
                                      {wasCopied ? 'Copied!' : 'Copy'}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}

                            {/* Upload drop zone */}
                            <div
                              onClick={() => attachmentInputRef.current?.click()}
                              className={`rounded-2xl border border-dashed flex flex-col items-center justify-center py-8 px-4 gap-2 cursor-pointer transition-all ${
                                uploadingAttachment
                                  ? 'border-red-500/30 bg-red-500/5'
                                  : 'border-neutral-800 bg-neutral-900/20 hover:bg-neutral-900/40 hover:border-neutral-600'
                              }`}
                            >
                              <div className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-colors ${
                                uploadingAttachment ? 'bg-red-500/10' : 'bg-neutral-800'
                              }`}>
                                <Camera size={18} className={uploadingAttachment ? 'text-red-400 animate-pulse' : 'text-neutral-500'} />
                              </div>
                              <div className="text-center">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">
                                  {uploadingAttachment ? 'Uploading to CDN...' : 'Upload Execution Screenshot'}
                                </p>
                                <p className="text-[9px] text-neutral-600 mt-0.5">Click to browse · max 5MB · hosted on our CDN</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column: Chart / Notes Workspace */}
                    <div className="hidden md:flex flex-1 bg-black flex-col overflow-hidden">
                      {/* Right Column Workspace Tabs */}
                      <div className="flex border-b border-neutral-800 p-2 bg-[#0F0F0F] justify-start gap-2">
                        {['Chart', 'Notes', 'Running P&L'].map((tab) => (
                          <button
                            key={tab}
                            onClick={() => setRightTab(tab as any)}
                            className={`px-6 py-2 text-xs font-bold rounded-lg transition-all ${
                              rightTab === tab 
                                ? 'bg-neutral-800 text-white' 
                                : 'text-neutral-500 hover:text-neutral-300'
                            }`}
                          >
                            {tab}
                          </button>
                        ))}
                      </div>

                      {/* Right Column Contents */}
                      <div className="flex-1 w-full relative overflow-hidden bg-[#0A0A0A]">
                        <div 
                          className={`w-full h-full flex flex-col overflow-y-auto bg-[#0A0A0A] ${rightTab !== 'Chart' ? 'hidden' : ''}`}
                          data-lenis-prevent
                        >
                          {/* Chart — DexScreener for DEX trades, TradingView for CEX */}
                          <div className="w-full h-[460px] bg-black relative flex-shrink-0 border-b border-neutral-800 group/chart">
                            {selectedTrade.is_dex && isResolvingPair ? (
                              <div className="w-full h-full flex flex-col items-center justify-center text-neutral-500 bg-[#0E0E0E] gap-2 absolute inset-0">
                                <Loader2 className="animate-spin text-purple-500" size={24} />
                                <span className="text-[10px] uppercase font-bold tracking-wider">Resolving DEX Pair Chart...</span>
                              </div>
                            ) : selectedTrade.is_dex && resolvedPairAddress && resolvedChainId ? (
                              <iframe
                                title="DexScreener Chart"
                                src={`https://dexscreener.com/${resolvedChainId}/${resolvedPairAddress}?embed=1&theme=dark&info=0`}
                                className="w-full h-full border-0 absolute inset-0"
                                allowFullScreen
                              />
                            ) : selectedTrade.is_dex ? (
                              <div className="w-full h-full flex flex-col items-center justify-center text-neutral-500 bg-[#0E0E0E] gap-2 absolute inset-0 p-4 text-center">
                                <span className="text-[10px] uppercase font-bold tracking-wider text-red-400">Could not resolve DEX Pair Chart</span>
                                <span className="text-[9px] text-neutral-600">Missing pair details for {selectedTrade.pairs}</span>
                              </div>
                            ) : (
                              <div className="w-full h-full absolute inset-0">
                                <TradingViewWidget
                                  symbol={getTradingViewSymbol(selectedTrade.pairs)}
                                  containerId="tv_chart_expanded_detail"
                                />
                              </div>
                            )}
                          </div>


                          {/* Notes section below chart */}
                          <TradeNoteEditor
                            tradeId={selectedTrade.id}
                            initialRemarks={selectedTrade.remarks || ''}
                            onSave={handleSaveTrade}
                          />
                        </div>

                        {rightTab === 'Notes' && (
                          <div className="w-full h-full p-8 overflow-y-auto space-y-6 bg-[#0E0E0E]" data-lenis-prevent>
                            <div className="max-w-3xl">
                              <h3 className="text-sm font-black uppercase tracking-widest text-neutral-500 mb-4">Trade Remarks & Log</h3>
                              <TradeNotesTab
                                tradeId={selectedTrade.id}
                                initialRemarks={selectedTrade.remarks || ''}
                                onSave={handleSaveTrade}
                              />
                            </div>
                          </div>
                        )}

                        {rightTab === 'Running P&L' && (() => {
                          const ep = selectedTrade.entry_price != null ? parseFloat(selectedTrade.entry_price) : null;
                          const xp = selectedTrade.exit_price != null ? parseFloat(selectedTrade.exit_price) : null;
                          const qty = selectedTrade.position_size != null ? parseFloat(selectedTrade.position_size) : null;
                          const fee = selectedTrade.fee != null ? parseFloat(selectedTrade.fee) : 0;
                          const comm = selectedTrade.commission != null ? parseFloat(selectedTrade.commission) : 0;
                          const totalFee = fee + comm;

                          // For OPEN DEX trades, use live price from DexScreener as exit price
                          const effectiveExitPrice = (selectedTrade.is_dex && selectedTrade.status === 'OPEN' && dexLivePrice)
                            ? dexLivePrice
                            : xp;

                          // Calculate live if we have all data, otherwise use stored values
                          const grossPnlCalc = (ep != null && effectiveExitPrice != null && qty != null)
                            ? (effectiveExitPrice - ep) * qty
                            : null;
                          const netPnlCalc = grossPnlCalc != null ? grossPnlCalc - totalFee : null;

                          // Fallback to stored DB values
                          const storedNetPnl = selectedTrade.pnl_amount != null ? parseFloat(selectedTrade.pnl_amount) : null;
                          const storedRoi = selectedTrade.pnl_percentage != null ? parseFloat(selectedTrade.pnl_percentage) : null;

                          const netPnl = netPnlCalc ?? storedNetPnl;
                          const grossPnl = grossPnlCalc ?? (storedNetPnl != null ? storedNetPnl + totalFee : null);
                          const roi = netPnlCalc != null && ep != null && qty != null && ep * qty > 0
                            ? (netPnlCalc / (ep * qty)) * 100
                            : storedRoi;

                          const fmt = (n: number, dec = 2) => n.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
                          const isWin = netPnl != null ? netPnl >= 0 : selectedTrade.status === 'WIN';

                          // Only show empty state when there is truly no data at all
                          if (ep == null && xp == null && netPnl == null) {
                            return (
                              <div className="w-full h-full p-8 flex flex-col justify-center items-center text-neutral-500 gap-3">
                                <TrendingUp size={36} className="opacity-20" />
                                <p className="text-xs font-bold uppercase tracking-wider">Tidak ada data P&L</p>
                                <p className="text-[10px] text-neutral-600">Entry price belum tersedia untuk trade ini.</p>
                              </div>
                            );
                          }

                          return (
                            <div className="w-full h-full p-8 overflow-y-auto bg-[#0E0E0E] space-y-4" data-lenis-prevent>
                              <div className="flex items-center justify-between">
                                <h3 className="text-sm font-black uppercase tracking-widest text-neutral-500">P&L Breakdown</h3>
                                {selectedTrade.is_dex && selectedTrade.status === 'OPEN' && dexLivePrice && (
                                  <span className="flex items-center gap-1.5 text-[10px] font-bold text-green-400 bg-green-400/10 border border-green-400/20 px-2 py-1 rounded-full">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                                    Live · ${dexLivePrice.toFixed(6)}
                                  </span>
                                )}
                              </div>

                              {/* Net P&L Hero */}
                              <div className={`p-6 rounded-2xl border ${isWin ? 'bg-green-900/10 border-green-500/20' : 'bg-red-900/10 border-red-500/20'}`}>
                                <p className="text-[10px] font-black uppercase text-neutral-500 tracking-widest mb-1">Net P&L {selectedTrade.is_dex && selectedTrade.status === 'OPEN' ? '(Unrealized)' : ''}</p>
                                <p className={`text-4xl font-black font-mono tracking-tight ${isWin ? 'text-green-400' : 'text-red-400'}`}>
                                  {netPnl != null ? `${netPnl >= 0 ? '+' : ''}$${fmt(netPnl)}` : 'N/A'}
                                </p>
                                {roi != null && (
                                  <p className={`text-sm font-bold mt-1 font-mono ${isWin ? 'text-green-500/70' : 'text-red-500/70'}`}>
                                    {roi >= 0 ? '+' : ''}{fmt(roi, 2)}% ROI
                                  </p>
                                )}
                              </div>

                              {/* Breakdown rows */}
                              <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl overflow-hidden">
                                {[
                                  { label: 'Entry Price', value: ep != null ? `$${fmt(ep, 8)}` : 'N/A' },
                                  { label: 'Exit Price', value: xp != null ? `$${fmt(xp, 8)}` : 'Open' },
                                  { label: 'Position Size', value: qty != null ? qty.toString() : 'N/A' },
                                  { label: 'Gross P&L', value: grossPnl != null ? `${grossPnl >= 0 ? '+' : ''}$${fmt(grossPnl)}` : 'N/A', color: grossPnl != null ? (grossPnl >= 0 ? 'text-green-500' : 'text-red-500') : 'text-neutral-500' },
                                  { label: 'Total Fees', value: `-$${fmt(totalFee, 6)}`, color: 'text-amber-500' },
                                  { label: 'Net P&L', value: netPnl != null ? `${netPnl >= 0 ? '+' : ''}$${fmt(netPnl)}` : 'N/A', color: isWin ? 'text-green-500' : 'text-red-500' },
                                ].map(({ label, value, color }) => (
                                  <div key={label} className="flex justify-between items-center px-4 py-3 text-xs border-b border-neutral-800 last:border-0">
                                    <span className="text-neutral-500 font-bold uppercase tracking-wider">{label}</span>
                                    <span className={`font-black font-mono ${color || 'text-white'}`}>{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                  </div>

                  {/* Mobile Bottom Action Bar - Fixed at Bottom */}
                  {selectedTrade && (
                    <div className="md:hidden fixed bottom-0 left-0 right-0 z-[70] bg-neutral-900/95 backdrop-blur-xl border-t border-neutral-800 px-4 py-3 flex items-center justify-around gap-2">
                      {/* Chart Button */}
                      <button
                        onClick={() => setActiveModal('chart')}
                        className="flex-1 flex flex-col items-center justify-center gap-1 min-h-[52px] rounded-xl bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20 transition-colors active:scale-95"
                      >
                        <TrendingUp size={20} className="text-blue-400" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400">Chart</span>
                      </button>

                      {/* Notes Button */}
                      <button
                        onClick={() => setActiveModal('notes')}
                        className="flex-1 flex flex-col items-center justify-center gap-1 min-h-[52px] rounded-xl bg-purple-500/10 border border-purple-500/20 hover:bg-purple-500/20 transition-colors active:scale-95"
                      >
                        <FileText size={20} className="text-purple-400" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400">Notes</span>
                      </button>

                      {/* P&L Button */}
                      <button
                        onClick={() => setActiveModal('pnl')}
                        className="flex-1 flex flex-col items-center justify-center gap-1 min-h-[52px] rounded-xl bg-green-500/10 border border-green-500/20 hover:bg-green-500/20 transition-colors active:scale-95"
                      >
                        <DollarSign size={20} className="text-green-400" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-green-400">P&L</span>
                      </button>
                    </div>
                  )}

                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Mobile Fullscreen Modals */}
          {/* Chart Modal */}
          <AnimatePresence>
            {activeModal === 'chart' && selectedTrade && (
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="md:hidden fixed inset-0 z-[100] bg-black flex flex-col"
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-neutral-900/95 backdrop-blur-xl border-b border-neutral-800">
                  <div>
                    <h3 className="text-sm font-black text-white">{selectedTrade.pairs}</h3>
                    <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider">Price Chart</p>
                  </div>
                  <button
                    onClick={() => setActiveModal(null)}
                    className="w-10 h-10 rounded-xl bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center transition-colors active:scale-95"
                  >
                    <X size={20} className="text-neutral-400" />
                  </button>
                </div>

                {/* Chart Content - Fullscreen */}
                <div className="flex-1 w-full overflow-hidden bg-black">
                  {selectedTrade.is_dex && isResolvingPair ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-neutral-500 bg-[#0E0E0E] gap-2">
                      <Loader2 className="animate-spin text-purple-500" size={24} />
                      <span className="text-[10px] uppercase font-bold tracking-wider">Resolving DEX Pair Chart...</span>
                    </div>
                  ) : selectedTrade.is_dex && resolvedPairAddress && resolvedChainId ? (
                    <iframe
                      title="DexScreener Chart"
                      src={`https://dexscreener.com/${resolvedChainId}/${resolvedPairAddress}?embed=1&theme=dark&info=0`}
                      className="w-full h-full border-0"
                      allowFullScreen
                    />
                  ) : selectedTrade.is_dex ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-neutral-500 bg-[#0E0E0E] gap-2 p-4 text-center">
                      <span className="text-[10px] uppercase font-bold tracking-wider text-red-400">Could not resolve DEX Pair Chart</span>
                      <span className="text-[9px] text-neutral-600">Missing pair details for {selectedTrade.pairs}</span>
                    </div>
                  ) : (
                    <div className="w-full h-full">
                      <TradingViewWidget
                        symbol={getTradingViewSymbol(selectedTrade.pairs)}
                        containerId="tv_chart_fullscreen_modal"
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Notes Modal */}
          <AnimatePresence>
            {activeModal === 'notes' && selectedTrade && (
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="md:hidden fixed inset-0 z-[100] bg-black flex flex-col"
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-neutral-900/95 backdrop-blur-xl border-b border-neutral-800">
                  <div>
                    <h3 className="text-sm font-black text-white">{selectedTrade.pairs}</h3>
                    <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider">Trade Notes</p>
                  </div>
                  <button
                    onClick={() => setActiveModal(null)}
                    className="w-10 h-10 rounded-xl bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center transition-colors active:scale-95"
                  >
                    <X size={20} className="text-neutral-400" />
                  </button>
                </div>

                {/* Notes Content - Fullscreen */}
                <div className="flex-1 w-full overflow-y-auto bg-[#0E0E0E] p-4">
                  <TradeNoteEditor
                    tradeId={selectedTrade.id}
                    initialRemarks={selectedTrade.remarks || ''}
                    onSave={handleSaveTrade}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* P&L Modal */}
          <AnimatePresence>
            {activeModal === 'pnl' && selectedTrade && (() => {
              const ep = selectedTrade.entry_price != null ? parseFloat(selectedTrade.entry_price) : null;
              const xp = selectedTrade.exit_price != null ? parseFloat(selectedTrade.exit_price) : null;
              const qty = selectedTrade.position_size != null ? parseFloat(selectedTrade.position_size) : null;
              const fee = selectedTrade.fee != null ? parseFloat(selectedTrade.fee) : 0;
              const comm = selectedTrade.commission != null ? parseFloat(selectedTrade.commission) : 0;
              const totalFee = fee + comm;

              // For OPEN DEX trades, use live price from DexScreener as exit price
              const effectiveExitPrice = (selectedTrade.is_dex && selectedTrade.status === 'OPEN' && dexLivePrice)
                ? dexLivePrice
                : xp;

              // Calculate live if we have all data, otherwise use stored values
              const grossPnlCalc = (ep != null && effectiveExitPrice != null && qty != null)
                ? (effectiveExitPrice - ep) * qty
                : null;
              const netPnlCalc = grossPnlCalc != null ? grossPnlCalc - totalFee : null;

              // Fallback to stored DB values
              const storedNetPnl = selectedTrade.pnl_amount != null ? parseFloat(selectedTrade.pnl_amount) : null;
              const storedRoi = selectedTrade.pnl_percentage != null ? parseFloat(selectedTrade.pnl_percentage) : null;

              const netPnl = netPnlCalc ?? storedNetPnl;
              const grossPnl = grossPnlCalc ?? (storedNetPnl != null ? storedNetPnl + totalFee : null);
              const roi = netPnlCalc != null && ep != null && qty != null && ep * qty > 0
                ? (netPnlCalc / (ep * qty)) * 100
                : storedRoi;

              const fmt = (n: number, dec = 2) => n.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
              const isWin = netPnl != null ? netPnl >= 0 : selectedTrade.status === 'WIN';

              return (
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                  className="md:hidden fixed inset-0 z-[100] bg-black flex flex-col"
                >
                  {/* Modal Header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-neutral-900/95 backdrop-blur-xl border-b border-neutral-800">
                    <div>
                      <h3 className="text-sm font-black text-white">{selectedTrade.pairs}</h3>
                      <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider">P&L Breakdown</p>
                    </div>
                    <button
                      onClick={() => setActiveModal(null)}
                      className="w-10 h-10 rounded-xl bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center transition-colors active:scale-95"
                    >
                      <X size={20} className="text-neutral-400" />
                    </button>
                  </div>

                  {/* P&L Content - Fullscreen */}
                  <div className="flex-1 w-full overflow-y-auto bg-[#0E0E0E] p-4 space-y-4">
                    {/* Live Price Indicator */}
                    {selectedTrade.is_dex && selectedTrade.status === 'OPEN' && dexLivePrice && (
                      <div className="flex items-center justify-center">
                        <span className="flex items-center gap-1.5 text-[10px] font-bold text-green-400 bg-green-400/10 border border-green-400/20 px-3 py-2 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                          Live · ${dexLivePrice.toFixed(6)}
                        </span>
                      </div>
                    )}

                    {/* Net P&L Hero */}
                    <div className={`p-6 rounded-2xl border ${isWin ? 'bg-green-900/10 border-green-500/20' : 'bg-red-900/10 border-red-500/20'}`}>
                      <p className="text-[10px] font-black uppercase text-neutral-500 tracking-widest mb-1">Net P&L {selectedTrade.is_dex && selectedTrade.status === 'OPEN' ? '(Unrealized)' : ''}</p>
                      <p className={`text-4xl font-black font-mono tracking-tight ${isWin ? 'text-green-400' : 'text-red-400'}`}>
                        {netPnl != null ? `${netPnl >= 0 ? '+' : ''}$${fmt(netPnl)}` : 'N/A'}
                      </p>
                      {roi != null && (
                        <p className={`text-sm font-bold mt-1 font-mono ${isWin ? 'text-green-500/70' : 'text-red-500/70'}`}>
                          {roi >= 0 ? '+' : ''}{fmt(roi, 2)}% ROI
                        </p>
                      )}
                    </div>

                    {/* Breakdown rows */}
                    <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl overflow-hidden">
                      {[
                        { label: 'Entry Price', value: ep != null ? `$${fmt(ep, 8)}` : 'N/A' },
                        { label: 'Exit Price', value: xp != null ? `$${fmt(xp, 8)}` : 'Open' },
                        { label: 'Position Size', value: qty != null ? qty.toString() : 'N/A' },
                        { label: 'Gross P&L', value: grossPnl != null ? `${grossPnl >= 0 ? '+' : ''}$${fmt(grossPnl)}` : 'N/A', color: grossPnl != null ? (grossPnl >= 0 ? 'text-green-500' : 'text-red-500') : 'text-neutral-500' },
                        { label: 'Total Fees', value: `-$${fmt(totalFee, 6)}`, color: 'text-amber-500' },
                        { label: 'Net P&L', value: netPnl != null ? `${netPnl >= 0 ? '+' : ''}$${fmt(netPnl)}` : 'N/A', color: isWin ? 'text-green-500' : 'text-red-500' },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="flex justify-between items-center px-4 py-3.5 text-xs border-b border-neutral-800 last:border-0">
                          <span className="text-neutral-500 font-bold uppercase tracking-wider">{label}</span>
                          <span className={`font-black font-mono ${color || 'text-white'}`}>{value}</span>
                        </div>
                      ))}
                    </div>

                    {/* Empty state if no data */}
                    {ep == null && xp == null && netPnl == null && (
                      <div className="w-full py-12 flex flex-col justify-center items-center text-neutral-500 gap-3">
                        <TrendingUp size={36} className="opacity-20" />
                        <p className="text-xs font-bold uppercase tracking-wider">Tidak ada data P&L</p>
                        <p className="text-[10px] text-neutral-600">Entry price belum tersedia untuk trade ini.</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>

          {/* Edit Trade Modal */}
          <AnimatePresence>
            {editingTrade && (
              <EditTradeModal
                trade={editingTrade}
                onClose={() => setEditingTrade(null)}
                onSave={handleSaveTrade}
              />
            )}
          </AnimatePresence>

          {/* Settings Modal */}
          <SettingsModal
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
          />

          {/* Floating Success Toast */}
          <AnimatePresence>
            {successMessage && (
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[10002] bg-[#0E0E0E]/95 border border-green-500/30 rounded-2xl px-6 py-4 shadow-2xl backdrop-blur-md flex items-center gap-3 text-white"
              >
                <Check size={18} className="text-green-500" />
                <span className="text-xs font-bold">{successMessage}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Candle Replay Simulator */}
          <AnimatePresence>
            {replayTrade && (
              <ReplaySimulator
                trade={replayTrade}
                onClose={() => setReplayTrade(null)}
              />
            )}
          </AnimatePresence>

          {/* Interactive Onboarding Tour */}
          <ProductTour steps={tourSteps} tourKey="trades" />
        </main>
      </div>
    );
  };

  // --- Sub Components ---

  const TradeNoteEditor = ({ tradeId, initialRemarks, onSave }: { tradeId: number, initialRemarks: string, onSave: (id: number, data: any) => Promise<void> }) => {
    const [noteText, setNoteText] = useState(initialRemarks);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    return (
      <div className="flex-1 min-h-[240px] p-6 bg-[#0F0F0F] flex flex-col border-t border-neutral-900">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-400 flex items-center gap-1.5">
            <span>Notes</span>
            {saved && <span className="text-[10px] text-green-500 font-mono">Saved ✓</span>}
          </h3>
          <button
            onClick={async () => {
              if (saving) return;
              setSaving(true);
              try {
                await onSave(tradeId, { remarks: noteText || null });
                setSaved(true);
                setTimeout(() => setSaved(false), 2500);
              } finally { setSaving(false); }
            }}
            disabled={saving}
            className="px-3 py-1 bg-neutral-800 hover:bg-neutral-700 rounded-lg text-[10px] font-black uppercase tracking-wider text-neutral-300 hover:text-white transition-all disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
        <textarea
          className="w-full flex-1 bg-neutral-950 border border-neutral-800 rounded-xl p-4 text-xs text-neutral-300 focus:outline-none focus:border-neutral-700 leading-relaxed font-sans resize-none min-h-[120px]"
          placeholder="Write your trade remarks, execution details, or journal notes here..."
          value={noteText}
          onChange={e => { setNoteText(e.target.value); setSaved(false); }}
        />
      </div>
    );
  };

  const TradeNotesTab = ({ tradeId, initialRemarks, onSave }: { tradeId: number, initialRemarks: string, onSave: (id: number, data: any) => Promise<void> }) => {
    const [noteText, setNoteText] = useState(initialRemarks);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    return (
      <>
        <textarea
          className="w-full h-[300px] bg-neutral-950 border border-neutral-800 rounded-xl p-5 text-sm text-neutral-300 focus:outline-none focus:border-neutral-700 leading-relaxed font-sans"
          placeholder="Write your trade execution details, setups, and notes here..."
          value={noteText}
          onChange={e => { setNoteText(e.target.value); setSaved(false); }}
        />
        <button
          onClick={async () => {
            if (saving) return;
            setSaving(true);
            try {
              await onSave(tradeId, { remarks: noteText || null });
              setSaved(true);
              setTimeout(() => setSaved(false), 2500);
            } finally { setSaving(false); }
          }}
          disabled={saving}
          className="mt-4 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Notes'}
        </button>
      </>
    );
  };

  const ErrorBadge = ({ message }: { message?: string }) => {
    if (!message) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-1.5 bg-red-900/20 border border-red-900/50 rounded-lg px-2.5 py-2 mb-2"
      >
        <AlertCircle size={12} className="text-red-500 flex-shrink-0" />
        <span className="text-[9px] font-bold text-red-500">{message}</span>
      </motion.div>
    );
  };

  const PnlLineChart = ({ pnlPositive }: { pnlPositive: boolean }) => {
    const pathRef = useRef<SVGPathElement>(null);
    const wrapperRef = useRef<SVGSVGElement>(null);
    const [isDrawn, setIsDrawn] = useState(false);
    const [totalLength, setTotalLength] = useState(0);

    useEffect(() => {
      setIsDrawn(false);
      setTotalLength(0);
      if (!pathRef.current || !wrapperRef.current) return;
      const len = pathRef.current.getTotalLength();
      setTotalLength(len);
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setIsDrawn(true);
          observer.disconnect();
        }
      }, { threshold: 0.1 });
      observer.observe(wrapperRef.current);
      return () => observer.disconnect();
    }, [pnlPositive]);

    const stroke = pnlPositive ? '#22c55e' : '#ef4444';
    const gradId = pnlPositive ? 'greenGrad' : 'redGrad';
    const gradColor = pnlPositive ? '#22c55e' : '#ef4444';
    const oscKey = pnlPositive ? 'pnl-osc-pos' : 'pnl-osc-neg';

    return (
      <svg ref={wrapperRef} className="w-24 h-12" viewBox="0 0 100 40" preserveAspectRatio="none">
        {isDrawn && (
          <style>{`@keyframes ${oscKey}{0%{transform:translateY(-2px)}100%{transform:translateY(2px)}}.${oscKey}{animation:${oscKey} 2s ease-in-out infinite alternate;}`}</style>
        )}
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={gradColor} />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
        <path
          ref={pathRef}
          d="M0 35 L20 25 L40 30 L60 10 L80 15 L100 5"
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          style={{
            strokeDasharray: totalLength || undefined,
            strokeDashoffset: isDrawn ? 0 : totalLength || undefined,
            transition: 'stroke-dashoffset 1.5s ease-out',
          }}
        />
        {isDrawn && (
          <path
            d="M0 35 L20 25 L40 30 L60 10 L80 15 L100 5"
            fill="none"
            stroke={stroke}
            strokeWidth="2"
            opacity="0.4"
            className={oscKey}
            style={{ animationDelay: '1.5s' }}
          />
        )}
        <path d="M0 35 L20 25 L40 30 L60 10 L80 15 L100 5 L100 40 L0 40 Z" fill={`url(#${gradId})`} opacity="0.2" />
      </svg>
    );
  };

  const AnimatedCircle = ({ winRate }: { winRate: string }) => {
    const [phase, setPhase] = useState<'spin' | 'fill'>('spin');
    const [animatedValue, setAnimatedValue] = useState(0);
    const [displayPct, setDisplayPct] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const target = parseFloat(winRate) || 0;

    useEffect(() => {
      if (!containerRef.current) return;
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        setPhase('spin');
        setAnimatedValue(0);
        setDisplayPct(0);
        const t = setTimeout(() => {
          setPhase('fill');
          let rafId: number;
          const duration = 1000;
          const startTime = performance.now();
          const tick = (now: number) => {
            const progress = Math.min((now - startTime) / duration, 1);
            setAnimatedValue(progress * target);
            setDisplayPct(Math.round(progress * target));
            if (progress < 1) rafId = requestAnimationFrame(tick);
          };
          rafId = requestAnimationFrame(tick);
        }, 800);
        return () => clearTimeout(t);
      }, { threshold: 0.1 });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }, [target]);

    return (
      <div ref={containerRef} className="w-10 h-10 md:w-12 md:h-12 relative flex-shrink-0">
        <style>{`@keyframes ac-spin{to{transform:rotate(360deg)}}`}</style>
        <svg className="w-full h-full" viewBox="0 0 36 36">
          <circle cx="18" cy="18" r="16" fill="none" stroke="#262626" strokeWidth="3" />
          {phase === 'spin' ? (
            <circle
              cx="18" cy="18" r="16" fill="none" stroke="#737373" strokeWidth="3"
              strokeDasharray="100, 0"
              strokeLinecap="round"
              style={{ animation: 'ac-spin 0.6s linear infinite', transformOrigin: '18px 18px' }}
            />
          ) : (
            <circle
              cx="18" cy="18" r="16" fill="none" stroke="#22c55e" strokeWidth="3"
              strokeDasharray={`${animatedValue}, 100`}
              strokeLinecap="round"
            />
          )}
          <text x="18" y="22" textAnchor="middle" fontSize="8" fontWeight="900" fill={phase === 'spin' ? '#737373' : '#22c55e'} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {displayPct}%
          </text>
        </svg>
      </div>
    );
  };

  const AnimatedLossCircle = ({ lossRate }: { lossRate: number }) => {
    const [phase, setPhase] = useState<'spin' | 'fill'>('spin');
    const [animatedValue, setAnimatedValue] = useState(0);
    const [displayPct, setDisplayPct] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!containerRef.current) return;
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        setPhase('spin');
        setAnimatedValue(0);
        setDisplayPct(0);
        const t = setTimeout(() => {
          setPhase('fill');
          let rafId: number;
          const duration = 1000;
          const startTime = performance.now();
          const tick = (now: number) => {
            const progress = Math.min((now - startTime) / duration, 1);
            setAnimatedValue(progress * lossRate);
            setDisplayPct(Math.round(progress * lossRate));
            if (progress < 1) rafId = requestAnimationFrame(tick);
          };
          rafId = requestAnimationFrame(tick);
        }, 800);
        return () => clearTimeout(t);
      }, { threshold: 0.1 });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }, [lossRate]);

    return (
      <div ref={containerRef} className="w-10 h-10 md:w-12 md:h-12 relative flex-shrink-0">
        <svg className="w-full h-full" viewBox="0 0 36 36">
          <circle cx="18" cy="18" r="16" fill="none" stroke="#262626" strokeWidth="3" />
          {phase === 'spin' ? (
            <circle
              cx="18" cy="18" r="16" fill="none" stroke="#737373" strokeWidth="3"
              strokeDasharray="100, 0"
              strokeLinecap="round"
              style={{ animation: 'ac-spin 0.6s linear infinite', transformOrigin: '18px 18px' }}
            />
          ) : (
            <circle
              cx="18" cy="18" r="16" fill="none" stroke="#ef4444" strokeWidth="3"
              strokeDasharray={`${animatedValue}, 100`}
              strokeLinecap="round"
            />
          )}
          <text x="18" y="22" textAnchor="middle" fontSize="8" fontWeight="900" fill={phase === 'spin' ? '#737373' : '#ef4444'}>
            {displayPct}%
          </text>
        </svg>
      </div>
    );
  };

  const AnimatedNumber = ({ target, decimals = 0 }: { target: number; decimals?: number }) => {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
      let rafId: number;
      const duration = 1000;
      const startTime = performance.now();
      const tick = (now: number) => {
        const progress = Math.min((now - startTime) / duration, 1);
        setDisplay(parseFloat((progress * target).toFixed(decimals)));
        if (progress < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafId);
    }, [target, decimals]);
    return <>{decimals > 0 ? display.toFixed(decimals) : Math.round(display)}</>;
  };

  const ProfitFactorNumber = ({ target }: { target: number }) => {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
      if (target >= 99.99) return;
      let rafId: number;
      const duration = 1800;
      const startTime = performance.now();
      const tick = (now: number) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(2, -10 * progress);
        setDisplay(eased * target);
        if (progress < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafId);
    }, [target]);

    if (target >= 99.99) {
      return <>MAX</>;
    }
    return <>{display.toFixed(2)}</>;
  };

  const StatCard = ({ title, value, percent, positive, sub, chart }: any) => {
    const [displayValue, setDisplayValue] = useState<string>('');
    const isDarkMode = useTheme();

    useEffect(() => {
      if (typeof value === 'string' && (value.includes('$') || value.includes('%'))) {
        const numStr = value.replace(/[^0-9.-]/g, '');
        const target = parseFloat(numStr);
        if (!isNaN(target)) {
          let start = 0;
          const duration = 1400;
          const steps = 50;
          const stepDuration = duration / steps;
          let step = 0;
          const interval = setInterval(() => {
            step++;
            const progress = step / steps;
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = target * eased;
            if (value.includes('%')) {
              setDisplayValue(`${current.toFixed(1)}%`);
            } else {
              const prefix = value.startsWith('+') ? '+' : value.startsWith('-') ? '-' : '';
              setDisplayValue(`${prefix}$${Math.abs(current).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);
            }
            if (step >= steps) {
              setDisplayValue(value);
              clearInterval(interval);
            }
          }, stepDuration);
          return () => clearInterval(interval);
        }
      }
      setDisplayValue(typeof value === 'string' ? value : '');
    }, [value]);

    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
        className={`border rounded-2xl p-3 md:p-6 flex flex-col justify-between transition-all group h-full ${
          isDarkMode
            ? 'bg-[#111] border-neutral-800 hover:border-neutral-700'
            : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'
        }`}
      >
        <div className="flex justify-between items-start mb-4 min-w-0">
          <p className={`text-[9px] md:text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>{title}</p>
          <div className="flex-shrink-0">{chart}</div>
        </div>
        <div>
          <h3 className={`text-base md:text-2xl font-black tracking-tighter truncate ${isDarkMode ? 'text-white' : 'text-black'}`}>
            {typeof value === 'string' ? displayValue || value : value}
          </h3>
          {percent && <p className={`text-[10px] font-black mt-1 ${positive ? 'text-green-500' : 'text-red-500'}`}>{percent} since last week</p>}
          {sub && <p className={`text-[10px] font-bold mt-1 uppercase tracking-tight ${isDarkMode ? 'text-neutral-600' : 'text-gray-500'}`}>{sub}</p>}
        </div>
      </motion.div>
    );
  };

  interface FilterDropdownProps {
    label: string;
    value: string | string[];
    options: { id: string; label: string; icon?: string | null; count?: number; color?: string }[];
    onChange: (id: any) => void;
    multiple?: boolean;
  }

  const FilterDropdown: React.FC<FilterDropdownProps> = ({ label, value, options, onChange, multiple = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const isDarkMode = useTheme();
    const ref = React.useRef<HTMLDivElement>(null);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

    useEffect(() => {
      const handleOutsideClick = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    useEffect(() => {
      if (isOpen && ref.current) {
        requestAnimationFrame(() => {
          if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const menuWidth = 220;
            const viewportWidth = window.innerWidth;
            const clampedWidth = Math.min(menuWidth, viewportWidth - 16);
            const left = rect.left + clampedWidth > viewportWidth
              ? Math.max(8, viewportWidth - clampedWidth - 8)
              : Math.max(8, rect.left);
            setMenuStyle({
              position: 'fixed',
              top: rect.bottom + 8,
              left,
              zIndex: 99999,
              minWidth: 180,
              maxWidth: Math.min(220, window.innerWidth - 16),
              width: Math.min(220, window.innerWidth - 16),
            });
          }
        });
      }
    }, [isOpen]);

    const selectedOptionIds = Array.isArray(value) ? value : [value];

    let displayLabel = '';
    if (selectedOptionIds.includes('All')) {
      displayLabel = label === 'Exchange' ? 'All Exchanges' : 'All';
    } else if (selectedOptionIds.length === 1) {
      const opt = options.find(o => o.id === selectedOptionIds[0]);
      displayLabel = opt ? (opt.id === 'Manual' ? 'Manual' : opt.label.split(' - ')[0]) : selectedOptionIds[0];
    } else {
      displayLabel = `${selectedOptionIds.length} Selected`;
    }

    const singleSelectedOpt = selectedOptionIds.length === 1 ? options.find(o => o.id === selectedOptionIds[0]) : null;

    const handleOptionClick = (optId: string) => {
      if (!multiple) {
        onChange(optId);
        setIsOpen(false);
        return;
      }
      if (optId === 'All') {
        onChange(['All']);
      } else {
        let next = selectedOptionIds.filter(id => id !== 'All');
        if (next.includes(optId)) {
          next = next.filter(id => id !== optId);
        } else {
          next.push(optId);
        }
        if (next.length === 0) next = ['All'];
        onChange(next);
      }
    };

    const menuContent = (
      <motion.div
        key="dropdown-menu"
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 5 }}
        style={menuStyle}
        className={`rounded-xl overflow-hidden shadow-2xl p-1 space-y-0.5 ${
          isDarkMode 
            ? 'bg-[#111] border border-neutral-800' 
            : 'bg-white border border-gray-200'
        }`}
      >
        {options.map((opt) => {
          const isSelected = selectedOptionIds.includes(opt.id);
          return (
            <button
              key={opt.id}
              onClick={(e) => {
                e.stopPropagation();
                handleOptionClick(opt.id);
              }}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-xs font-bold transition-all ${
                isDarkMode 
                  ? isSelected ? 'bg-neutral-800 text-white' : 'text-neutral-400 hover:bg-neutral-900 hover:text-white'
                  : isSelected ? 'bg-gray-100 text-black' : 'text-gray-600 hover:bg-gray-50 hover:text-black'
              }`}
            >
              <div className="flex items-center gap-2.5">
                {multiple && (
                  <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all ${
                    isDarkMode
                      ? isSelected ? 'border-red-600 bg-red-600 text-white' : 'border-neutral-700 bg-neutral-950'
                      : isSelected ? 'border-red-600 bg-red-600 text-white' : 'border-gray-300 bg-gray-50'
                  }`}>
                    {isSelected && <Check size={10} strokeWidth={4} />}
                  </div>
                )}
                {opt.icon ? (
                  <img src={opt.icon} alt={opt.label} className="w-3.5 h-3.5 object-contain" />
                ) : opt.id === 'Manual' ? (
                  <span className="text-emerald-500 text-[10px]">✍️</span>
                ) : (
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: opt.color || '#fff' }} />
                )}
                <span>{opt.id === 'All' ? (label === 'Exchange' ? 'All Exchanges' : 'All') : opt.id === 'Manual' ? 'Manual Trades' : opt.label.split(' - ')[0]}</span>
              </div>
              {opt.count !== undefined && (
                <span className={`px-1.5 py-0.5 rounded text-[8px] font-black ${
                  isDarkMode
                    ? isSelected ? 'bg-red-600/20 text-red-500' : 'bg-neutral-900 text-neutral-500'
                    : isSelected ? 'bg-red-600/20 text-red-500' : 'bg-gray-100 text-gray-600'
                }`}>
                  {opt.count}
                </span>
              )}
            </button>
          );
        })}
      </motion.div>
    );

    return (
      <div ref={ref} className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1.5 ${
            isDarkMode
              ? 'bg-[#1a1a1a] border border-neutral-800 text-white hover:bg-neutral-800'
              : 'bg-white border border-gray-200 text-black hover:bg-gray-50'
          }`}
        >
          <span className={isDarkMode ? 'text-neutral-600 font-normal' : 'text-gray-500 font-normal'}>{label}:</span>
          {singleSelectedOpt?.icon && (
            <img src={singleSelectedOpt.icon} alt={displayLabel} className="w-3.5 h-3.5 object-contain" />
          )}
          {singleSelectedOpt?.id === 'Manual' && <span className="text-emerald-500 text-[10px]">✍️</span>}
          <span className={isDarkMode ? 'text-white' : 'text-black'}>{displayLabel}</span>
          <ChevronRight size={12} className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''} ${isDarkMode ? 'text-neutral-600' : 'text-gray-400'}`} />
        </button>

        {typeof document !== 'undefined' && ReactDOM.createPortal(
          <AnimatePresence>
            {isOpen && menuContent}
          </AnimatePresence>,
          document.body
        )}
      </div>
    );
  };

  const DetailCard = ({ label, value, sub, positive }: any) => (
    <div className="bg-[#1A1A1A] p-4 rounded-xl border border-neutral-800">
      <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">{label}</p>
      <div className="flex items-baseline gap-2">
        <p className={`text-lg font-bold font-mono ${positive === true ? 'text-green-500' : positive === false ? 'text-red-500' : 'text-white'}`}>{value}</p>
        {sub && <span className="text-[10px] font-bold text-neutral-500 font-mono">{sub}</span>}
      </div>
    </div>
  );

  // --- Candle Replay Simulator Sub Component ---

  interface ReplaySimulatorProps {
    trade: any;
    onClose: () => void;
  }

  const ReplaySimulator: React.FC<ReplaySimulatorProps> = ({ trade, onClose }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [speed, setSpeed] = useState(800); // ms per step

    const isWin = trade.status === 'WIN';
    const isLong = trade.direction === 'LONG';
    const entryPrice = parseFloat(trade.entry_price) || 100;
    const exitPrice = parseFloat(trade.exit_price) || (isWin ? entryPrice * 1.05 : entryPrice * 0.95);
    
    const candles = React.useMemo(() => {
      const list = [];
      let price = entryPrice * 0.98; // Start slightly below entry
      
      for (let i = 0; i < 15; i++) {
        let open = price;
        let close = price;
        let event = '';

        if (i < 4) {
          // Consolidation going up to entry
          close = open + (entryPrice - open) * (i / 4);
          event = i === 3 ? 'ENTRY_TRIGGERED' : '';
        } else if (i < 11) {
          // Fluctuations
          if (isWin) {
            close = open + (exitPrice - entryPrice) * ((i - 3) / 8) + (Math.random() - 0.5) * (entryPrice * 0.005);
          } else {
            close = open - (entryPrice - exitPrice) * ((i - 3) / 8) + (Math.random() - 0.5) * (entryPrice * 0.005);
          }
        } else if (i < 14) {
          // Push to exit
          close = exitPrice;
          event = i === 13 ? 'EXIT_TRIGGERED' : '';
        } else {
          // Post-exit candle
          close = open + (Math.random() - 0.5) * (entryPrice * 0.005);
        }

        const high = Math.max(open, close) + (Math.random() * (entryPrice * 0.003));
        const low = Math.min(open, close) - (Math.random() * (entryPrice * 0.003));
        price = close;

        list.push({
          open,
          close,
          high,
          low,
          isGreen: close >= open,
          event
        });
      }
      return list;
    }, [isWin, entryPrice, exitPrice]);

    useEffect(() => {
      if (!isPlaying) return;

      const timer = setInterval(() => {
        setCurrentStep(prev => {
          if (prev < candles.length - 1) {
            return prev + 1;
          } else {
            setIsPlaying(false);
            return prev;
          }
        });
      }, speed);

      return () => clearInterval(timer);
    }, [isPlaying, speed, candles.length]);

    const activeCandle = candles[currentStep];
    const currentPrice = activeCandle ? activeCandle.close : entryPrice;
    
    let runningPnl = 0;
    const size = parseFloat(trade.position_size) || 1;
    
    if (currentStep >= 3) {
      if (isLong) {
        runningPnl = (currentPrice - entryPrice) * size;
      } else {
        runningPnl = (entryPrice - currentPrice) * size;
      }
    }
    
    if (currentStep >= 13) {
      runningPnl = parseFloat(trade.pnl_amount);
    }

    return (
      <div className="fixed inset-0 z-[10005] bg-black/95 backdrop-blur-xl flex flex-col font-sans text-white select-none">
        {/* Header */}
        <div className="px-8 py-5 border-b border-neutral-900 flex justify-between items-center bg-[#050505] shrink-0">
          <div className="flex items-center gap-4">
            <span className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-500 text-[10px] font-black uppercase tracking-widest rounded-lg">
              PREMIUM REPLAY
            </span>
            <h2 className="text-lg font-black tracking-tight">{trade.pairs} - Trade Candle Replay</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-900 border border-neutral-800 rounded-xl transition-all text-neutral-500 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {/* Main workspace */}
        <div className="flex-1 p-8 flex flex-col gap-6 overflow-y-auto max-w-7xl mx-auto w-full">
          {/* Analytics bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 shrink-0">
            <div className="bg-neutral-900/40 border border-neutral-800/80 rounded-2xl p-5">
              <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Status</p>
              <p className="text-xs font-black uppercase tracking-wider text-neutral-400 font-mono">
                {currentStep < 3 ? 'PRE-ENTRY CONSOLIDATION' : currentStep < 13 ? 'ACTIVE POSITION LIVE' : 'TRADE COMPLETED'}
              </p>
            </div>
            <div className="bg-neutral-900/40 border border-neutral-800/80 rounded-2xl p-5">
              <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Current Price</p>
              <p className="text-lg font-bold font-mono text-white">${currentPrice.toFixed(4)}</p>
            </div>
            <div className="bg-neutral-900/40 border border-neutral-800/80 rounded-2xl p-5">
              <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Entry Price</p>
              <p className="text-lg font-bold font-mono text-neutral-400">${entryPrice.toFixed(4)}</p>
            </div>
            <div className="bg-neutral-900/40 border border-neutral-800/80 rounded-2xl p-5">
              <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Running P&L</p>
              <p className={`text-lg font-black font-mono ${runningPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {runningPnl >= 0 ? '+' : ''}${runningPnl.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Visual Candle Chart Simulator */}
          <div className="h-96 bg-neutral-950 border border-neutral-900 rounded-3xl p-8 relative flex items-end justify-between overflow-hidden shadow-2xl shrink-0">
            {/* Grid lines */}
            <div className="absolute inset-x-0 top-1/4 h-px bg-neutral-900/10" />
            <div className="absolute inset-x-0 top-2/4 h-px bg-neutral-900/10" />
            <div className="absolute inset-x-0 top-3/4 h-px bg-neutral-900/10" />

            {/* Reference price lines */}
            <div className="absolute inset-x-0 top-[60%] h-px border-t border-dashed border-blue-500/30 flex justify-between px-4 z-10">
              <span className="text-[9px] font-bold text-blue-500 uppercase tracking-widest bg-black px-1.5 py-0.5 rounded border border-blue-500/20 -translate-y-1/2">
                Entry Level: ${entryPrice.toFixed(2)}
              </span>
            </div>
            <div className="absolute inset-x-0 top-[30%] h-px border-t border-dashed border-red-500/30 flex justify-between px-4 z-10">
              <span className="text-[9px] font-bold text-red-500 uppercase tracking-widest bg-black px-1.5 py-0.5 rounded border border-red-500/20 -translate-y-1/2">
                Exit Level: ${exitPrice.toFixed(2)}
              </span>
            </div>

            {/* Render candles up to currentStep */}
            <div className="w-full h-64 flex items-end justify-between relative z-20 px-4 sm:px-8">
              {candles.map((candle, idx) => {
                const isRendered = idx <= currentStep;
                if (!isRendered) return <div key={idx} className="flex-1 opacity-0" />;

                const minPrice = entryPrice * 0.975;
                const maxPrice = entryPrice * 1.075;
                const diff = maxPrice - minPrice;
                
                const pctOpen = ((candle.open - minPrice) / diff) * 100;
                const pctClose = ((candle.close - minPrice) / diff) * 100;
                const pctHigh = ((candle.high - minPrice) / diff) * 100;
                const pctLow = ((candle.low - minPrice) / diff) * 100;

                const bottom = Math.min(pctOpen, pctClose);
                const height = Math.max(3, Math.abs(pctOpen - pctClose));
                const wickBottom = pctLow;
                const wickHeight = Math.max(1, pctHigh - pctLow);

                return (
                  <div key={idx} className="flex-1 flex flex-col items-center justify-end h-full relative group">
                    {/* Event labels */}
                    {candle.event === 'ENTRY_TRIGGERED' && (
                      <div className="absolute bottom-[105%] bg-blue-500 text-black font-extrabold text-[8px] px-2 py-0.5 rounded-full uppercase tracking-wider animate-bounce z-30">
                        ENTRY
                      </div>
                    )}
                    {candle.event === 'EXIT_TRIGGERED' && (
                      <div className="absolute bottom-[105%] bg-red-500 text-white font-extrabold text-[8px] px-2 py-0.5 rounded-full uppercase tracking-wider animate-bounce z-30">
                        EXIT
                      </div>
                    )}

                    {/* Wick */}
                    <div 
                      style={{ bottom: `${wickBottom}%`, height: `${wickHeight}%` }}
                      className={`absolute w-0.5 ${candle.isGreen ? 'bg-green-500' : 'bg-red-500'}`}
                    />

                    {/* Body */}
                    <div 
                      style={{ bottom: `${bottom}%`, height: `${height}%` }}
                      className={`w-3 sm:w-6 rounded-sm absolute ${candle.isGreen ? 'bg-green-500' : 'bg-red-500'} shadow-lg group-hover:scale-110 transition-all`}
                    />

                    {/* Hover tooltips */}
                    <div className="absolute bottom-full mb-6 z-30 bg-neutral-900 border border-neutral-800 rounded-lg p-2 text-[9px] pointer-events-none opacity-0 group-hover:opacity-100 transition-all font-mono whitespace-nowrap flex flex-col gap-0.5">
                      <p className="text-white">O: {candle.open.toFixed(4)}</p>
                      <p className="text-white">C: {candle.close.toFixed(4)}</p>
                      <p className="text-white">H: {candle.high.toFixed(4)}</p>
                      <p className="text-white">L: {candle.low.toFixed(4)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Controls bar */}
          <div className="bg-neutral-950 border border-neutral-900 rounded-3xl p-6 flex flex-col sm:flex-row justify-between items-center gap-6 shrink-0">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentStep(0)}
                className="px-4 py-2.5 bg-neutral-900 border border-neutral-800 hover:text-white rounded-xl text-[10px] font-black uppercase tracking-wider transition-all outline-none"
              >
                Reset
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all outline-none ${isPlaying ? 'bg-amber-600 text-white hover:bg-amber-500' : 'bg-red-600 text-white hover:bg-red-500'} shadow-lg`}
              >
                {isPlaying ? 'Pause' : 'Play Simulation'}
              </button>
              <button
                onClick={() => setCurrentStep(prev => Math.min(candles.length - 1, prev + 1))}
                disabled={isPlaying}
                className="px-4 py-2.5 bg-neutral-900 border border-neutral-800 hover:text-white rounded-xl text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-30 outline-none"
              >
                Step Forward
              </button>
            </div>

            <div className="flex items-center gap-4 w-full sm:w-64">
              <span className="text-[10px] font-black uppercase text-neutral-500 tracking-wider whitespace-nowrap">Speed:</span>
              <input 
                type="range"
                min="300"
                max="2000"
                step="100"
                value={speed}
                onChange={e => setSpeed(parseInt(e.target.value))}
                className="w-full accent-red-600 cursor-pointer h-1 bg-neutral-800 rounded-lg appearance-none"
              />
              <span className="text-xs font-bold text-neutral-400 font-mono">{(speed/1000).toFixed(1)}s</span>
            </div>
          </div>
        </div>
      </div>
    );
  };
export default TradesPage;
